import { Controller } from "@hotwired/stimulus"
import {
  buildFrameTimeline,
  frameAtGlobalMs,
  globalMsForStripX,
  positionForGlobalMs,
} from "../lib/animatic_timeline.mjs"

// Per-character subtitle colour — the sung fill + the speaker label take the
// character's hue so it's clear who is speaking. Bright values that read over
// the dark caption scrim (the un-sung text stays solid white).
const SPEAKER_COLORS = {
  Derek: "#ffcf4d",    // gold
  Fern: "#c59cff",     // lavender / purple
  Gus: "#b9b2c4",      // soft grey (the grumpy grey sheep)
  Plume: "#5fd06a",    // green
  Marigold: "#ff9a3d", // orange
  Wisp: "#5ab0ff",     // blue
  Dolly: "#7fe3e8",         // cyan, matching her indicator lights
  "Female Exec": "#e59bb4", // Episode 002's studio executives — not flock, so
  "Male Exec": "#9fb4e8",   // cooler and flatter than the sheep colours
}
const DEFAULT_FILL_COLOR = "#ffcf4d"

// Perceptual lead: the fill runs this many ms AHEAD of the audio so the highlight
// feels "on" the sound. Display-only — stored timings stay exact. 200ms chosen
// from the caption-timing A/B (docs/review/CAPTION_TIMING_AB.md): WhisperX edges
// land a touch late, so the karaoke wants more lead than a naive 100ms.
const PLAYBACK_LEAD_MS = 200
const IDLE_MEDIA_REFRESH_MS = 5 * 60 * 1000
const MEDIA_REFRESH_MIN_INTERVAL_MS = 10 * 1000

// Tiny silent WAV (44-byte header, no samples) used only to "unlock" the shared
// audio element inside the first Play gesture on iOS Safari, so the clock-driven
// clips that follow are allowed to play. See unlockAudio().
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA="

// Worst-unresolved severity → strip-marker colour.
const SEV_COLOR = {
  critical: "var(--ms-rose)",
  major: "var(--ms-amber)",
  moderate: "var(--ms-amber-deep)",
  minor: "var(--ms-green)",
  trivial: "var(--ms-faint)",
  note: "var(--ms-purple)",
}

// Strip metrics (cozy density, per strip.jsx stripMetrics).
const STRIP = { h: 64, min: 32, max: 108, gap: 4, lane: 6, label: 46, padX: 9, track: 24, laneH: 40 }

// Layout-resizer persistence + clamps (B1–B3).
const RAIL_WIDTH_KEY = "ms-studio:rail-width"
const RAIL_COLLAPSED_KEY = "ms-studio:rail-collapsed"
const STRIP_H_KEY = "ms-studio:strip-h"
const VIEWER_H_KEY = "ms-studio:viewer-h"
const AUDIO_TRACK_MUTED_KEY = "ms-studio:audio-track-muted"
const IMAGE_QUALITY_KEY = "ms-studio:image-quality"
const IMAGE_QUALITY_AUTO = "auto"
const IMAGE_QUALITY_MOBILE = "mobile"
const IMAGE_QUALITY_FULL = "full"
const RAIL_MIN = 280
// Wider default rail (live-use restructure): it now hosts the relocated header
// controls + the feedback composer + the comments, so it needs more room than
// the old comments-only 380px. RAIL_MAX is lifted so the default isn't clamped.
const RAIL_MAX = 680
const RAIL_DEFAULT = 580
const STRIP_H_MIN = 56
const STRIP_H_MAX = 190
const STRIP_H_DEFAULT = 64
// Viewer pane height (B3). When the user has dragged the divider we pin an
// explicit basis (px) so it survives even when the column overflows + scrolls;
// before any drag the viewer flexes to fill leftover space (default CSS).
const VIEWER_H_MIN = 150
const VIEWER_H_MAX = 900
const TIMELINE_GUTTER = 74
const AUDIO_TRACKS = [
  { key: "dialogue", label: "VO" },
  { key: "music", label: "Music" },
  { key: "effects", label: "FX" },
]

const isMusicCue = (cue) => /string|piano|cello|melody|pizz|swell|hum|score|music/i.test(cue || "")

export default class extends Controller {
  static targets = [
    "audio",
    "audioLabel",
    "captions",
    "captionCue",
    "captionDialogue",
    "captionEmpty",
    "changeContext",
    "changeTab",
    "frameLabel",
    "focusedPrompt",
    "noteAudioFilename",
    "noteBody",
    "noteContext",
    "noteFrameId",
    "noteFrameNumber",
    "noteImageAssetId",
    "noteAssetId",
    "noteShotId",
    "noteTimeMs",
    "noteAudioAssetId",
    "position",
    "preview",
    "referenceMaterial",
    "sourceBeat",
    "sourceExcerpt",
    "sourceFrameLabel",
    // new (S2–S7)
    "sequenceLabel",
    "viewerError",
    "loadingOverlay",
    "loadingLabel",
    "strip",
    "stripInner",
    "playButton",
    "replayButton",
    "muteButton",
    "muteIcon",
    "volumeRange",
    "speedSelect",
    "qualitySelect",
    "frameInput",
    "frameInputTotal",
    "zoomRange",
    "zoomLabel",
    "timelineMeta",
    "composerAnchor",
    "contextTab",
    "contextPane",
    "contextBody",
    "contextChevron",
    "compareSelect",
    "compareSummary",
    // Layout resizers (B1–B3)
    "rail",
    "railResizer",
    "railCollapse",
    "railHeaderToggle",
    "stripResizer",
    "viewerFrame",
    "viewerResizer",
  ]
  static values = {
    frameContext: Object,
    manifestUrl: String,
    stripZoom: { type: Number, default: 1 },
  }

  connect() {
    this.frames = []
    this.currentIndex = 0
    this.playingThrough = false
    this.advanceTimer = null
    this.speed = 1
    this.markers = {} // { frameNumber: { open, total, worst } } — set only by commentsChanged
    this.changedFrames = new Set()
    this.changeSets = { frames: new Set(), image: new Set(), audio: new Set(), duration: new Set() }
    this.compareChanges = new Map()
    this.compareVersion = null
    this.seekNonce = 0
    this.playingNonce = -1
    this.dragging = false
    this.scrubRaf = 0
    this.lastScrubProgress = null
    this.scrubMetrics = null
    this.lastMediaError = null
    this.debugEnabled = new URL(window.location.href).searchParams.get("animatic_debug") === "1"
    this.soundBlocked = false

    // Karaoke + clock state (preserved logic).
    this.karaokeRaf = null
    this.currentWords = []
    this.wordSpans = []
    this.fillUnits = []
    this.activeWordIndex = -1
    this.captionAudioEvent = null
    this.frameStartMs = []
    this.clockRaf = null
    this.frameStartedAt = null

    // Phase 2b: SFX / ambient / music laid on the global timeline. Each event owns
    // a preloaded <audio>; the play-through clock fires it when the global playhead
    // crosses its start. Lead-free (the timecode is truth) so effects land on time.
    this.audioEvents = []
    this.firedEvents = new Set()
    this.preloadedImages = new Set()
    this.warmedAudio = new Map()
    this.mediaRefreshToken = null
    this.mediaRefreshPromise = null
    this.lastMediaRefreshAt = 0
    this.hiddenAt = null
    this.audioTrackMuted = this.restoreAudioTrackMuted()
    this.imageQuality = this.restoreImageQuality()

    this.onPreviewError = () => this.handlePreviewError()
    this.previewTarget.addEventListener("error", this.onPreviewError)

    this.audioTarget.addEventListener("ended", () => {
      // Play-through is clock-driven (see playTick): a clip ending no longer
      // advances the timeline, so the listener only stops the karaoke fill.
      this.stopKaraoke()
    })
    this.onAudioError = () => this.handleMediaError("audio")
    this.audioTarget.addEventListener("error", this.onAudioError)
    this.audioTarget.addEventListener("pause", () => { this.stopKaraoke() })
    this.audioTarget.addEventListener("play", () => { this.frameStartedAt = performance.now(); this.startKaraoke(); this.startClock() })

    // Buffering indicator: while playing, if the current clip stalls waiting for
    // data, show "Loading audio…" so a silent-because-still-loading start (esp. the
    // iOS cold start) never reads as a bug; clear it once audio is actually flowing.
    this.audioTarget.addEventListener("waiting", () => { if (this.playingThrough) this.showBuffering() })
    this.audioTarget.addEventListener("stalled", () => { if (this.playingThrough) this.showBuffering() })
    this.audioTarget.addEventListener("playing", () => this.hideBuffering())
    this.audioTarget.addEventListener("canplay", () => {
      this.hideBuffering()
      this.warmUpcomingAudio(this.currentIndex)
    })

    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydown)

    this.onVisibilityChange = this.onVisibilityChange.bind(this)
    this.onWindowBlur = this.onWindowBlur.bind(this)
    this.onWindowFocus = this.onWindowFocus.bind(this)
    this.onPageShow = this.onPageShow.bind(this)
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    window.addEventListener("blur", this.onWindowBlur)
    window.addEventListener("focus", this.onWindowFocus)
    window.addEventListener("pageshow", this.onPageShow)

    // Keep the maximised class in sync if the user leaves native fullscreen via
    // Esc / the OS chrome.
    this.onFullscreenChange = () => {
      if (!document.fullscreenElement) this.element.classList.remove("is-maximised")
    }
    document.addEventListener("fullscreenchange", this.onFullscreenChange)

    // Drag listeners are bound once so they can be removed reliably.
    this.boundPointerMove = this.onPointerMove.bind(this)
    this.boundPointerUp = this.onPointerUp.bind(this)

    if (this.hasVolumeRangeTarget) this.audioTarget.volume = Number(this.volumeRangeTarget.value)
    this.syncQualitySelect()

    // Restore persisted layout (rail width/collapse, strip height, viewer pane)
    // before the manifest renders so the strip is built at the right height.
    this.stripHeight = STRIP_H_DEFAULT
    this.restoreLayout()

    this.loadManifest()
  }

  disconnect() {
    this.stopPlayback()
    this.stopKaraoke()
    this.stopClock()
    this.stopAudioEvents()
    this.cancelScheduledWaveforms()
    this.abortWaveformDecodes()
    this.thumbObserver?.disconnect()
    this.removeDragListeners()
    this.teardownLayoutResize()
    if (this.scrubRaf) { cancelAnimationFrame(this.scrubRaf); this.scrubRaf = 0 }
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    window.removeEventListener("blur", this.onWindowBlur)
    window.removeEventListener("focus", this.onWindowFocus)
    window.removeEventListener("pageshow", this.onPageShow)
    document.removeEventListener("fullscreenchange", this.onFullscreenChange)
    this.previewTarget.removeEventListener("error", this.onPreviewError)
    this.audioTarget.removeEventListener("error", this.onAudioError)
    // Close the decode-only AudioContext so each Turbo visit doesn't leak one.
    this.decodeCtx?.close()
    this.decodeCtx = null
  }

  async loadManifest({ refresh = false, snapshot = null } = {}) {
    try {
      const response = await fetch(this.manifestFetchUrl(refresh), { cache: refresh ? "no-store" : "no-cache" })
      if (!response.ok) throw new Error(`manifest ${response.status}`)
      const manifest = await response.json()
      let restoreSnapshot = snapshot
      if (restoreSnapshot && this.currentFrame?.number && this.currentFrame.number !== restoreSnapshot.frameNumber) {
        restoreSnapshot = this.mediaSnapshot()
      }

      this.manifest = manifest
      this.syncQualityOptions(manifest.imageQualities || [])
      this.frames = manifest.frames
      this.diff = manifest.diff || {}
      this.compareVersion = manifest.compareVersion || manifest.compare_version || this.selectedCompareVersion()
      this.changeSets = this.changeSetsFromDiff(this.diff)
      this.compareChanges = this.compareChangesFromDiff(this.diff)
      this.changedFrames = this.changeSets.frames
      this.syncCompareControls()
      // Whether ANY frame in this version carries caption data — drives the
      // graceful "no captions for this version" state vs. a per-frame gap.
      this.manifestHasCaptions = this.frames.some((f) => {
        const c = f.captions || {}
        return Boolean(c.cue) || (Array.isArray(c.dialogue?.words) && c.dialogue.words.length > 0)
      })
      this.rebuildTimeline()
      // Phase 2b: SFX / ambient / music + per-word clips laid on the global
      // timeline; fired by the play-through clock at their exact start.
      this.loadAudioEvents(manifest.audioEvents || [])
      if (this.hasViewerErrorTarget) this.viewerErrorTarget.hidden = true
      this.syncTimelineMeta()
      const initialIndex = restoreSnapshot ? this.indexForMediaSnapshot(restoreSnapshot) : this.initialFrameIndex()
      this.showFrame(initialIndex)
      this.renderStrip()
      this.scrollInitialFrameIntoView(initialIndex)
      if (restoreSnapshot) this.restoreMediaSnapshot(restoreSnapshot)
      this.renderDebugHud()
    } catch (error) {
      this.renderManifestError()
    }
  }

  rebuildTimeline() {
    this.timeline = buildFrameTimeline(this.frames || [])
    this.frameStartMs = this.timeline.entries.map((entry) => entry.startMs)
    this.totalMs = this.timeline.totalMs
  }

  manifestFetchUrl(refresh = false) {
    const url = new URL(this.manifestUrlValue, window.location.href)
    const compareVersion = this.selectedCompareVersion()
    if (compareVersion) url.searchParams.set("compare_version", compareVersion)
    else url.searchParams.delete("compare_version")
    if (refresh) url.searchParams.set("_ms_media", String(Date.now()))
    return url.toString()
  }

  onVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.hiddenAt = Date.now()
      return
    }
    this.refreshManifestIfIdle()
  }

  onWindowBlur() {
    this.hiddenAt ||= Date.now()
  }

  onWindowFocus() {
    this.refreshManifestIfIdle()
  }

  onPageShow(event) {
    if (event.persisted) this.refreshManifestMedia({ reason: "pageshow", force: true })
  }

  refreshManifestIfIdle() {
    if (!this.hiddenAt) return
    const hiddenFor = Date.now() - this.hiddenAt
    this.hiddenAt = null
    if (hiddenFor >= IDLE_MEDIA_REFRESH_MS) this.refreshManifestMedia({ reason: "resume" })
  }

  handleMediaError(reason) {
    if (!this.frames?.length) return
    this.lastMediaError = `${reason} at ${new Date().toISOString()}`
    this.showMediaRefreshing()
    this.refreshManifestMedia({ reason, force: true })
  }

  handlePreviewError() {
    const failedSrc = this.previewTarget.currentSrc || this.previewTarget.src
    window.setTimeout(() => {
      const currentSrc = this.previewTarget.currentSrc || this.previewTarget.src
      if (currentSrc !== failedSrc) return
      this.lastMediaError = `image load failed at ${new Date().toISOString()}`
      this.renderDebugHud()
      this.handleMediaError("preview-image")
    }, 250)
  }

  refreshManifestMedia({ force = false } = {}) {
    if (this.dragging || this.playingThrough) {
      this.pendingMediaRefresh = true
      return null
    }
    if (this.mediaRefreshPromise) {
      this._audioUnlocked = false
      this.unlockMainAudioSrc = null
      this.unlockMainAudioMuted = null
      return this.mediaRefreshPromise
    }
    const now = Date.now()
    if (!force && now - this.lastMediaRefreshAt < MEDIA_REFRESH_MIN_INTERVAL_MS) return null

    this.lastMediaRefreshAt = now
    const snapshot = this.mediaSnapshot()
    this.stopPlayback()
    this.clearMediaCaches()
    this.mediaRefreshToken = String(now)
    this._audioUnlocked = false
    this.unlockMainAudioSrc = null
    this.unlockMainAudioMuted = null

    this.showMediaRefreshing()
    this.mediaRefreshPromise = this.loadManifest({ refresh: true, snapshot })
      .finally(() => {
        this.mediaRefreshPromise = null
        this.hideBuffering()
        this.renderDebugHud()
      })
    return this.mediaRefreshPromise
  }

  clearMediaCaches() {
    this.preloadedImages = new Set()
    this.warmedAudio.forEach((audio) => {
      try { audio.pause() } catch (_) { /* ignore */ }
      try { audio.removeAttribute("src") } catch (_) { /* ignore */ }
    })
    this.warmedAudio = new Map()
    this.audioEvents.forEach((event) => {
      event.loadedSrc = null
      try { event.audio?.pause() } catch (_) { /* ignore */ }
      try { event.audio?.removeAttribute("src") } catch (_) { /* ignore */ }
    })
    this.cancelScheduledWaveforms()
    this.abortWaveformDecodes()
    this.waveformPeaks = new Map()
  }

  mediaSnapshot() {
    const frame = this.currentFrame
    const frameNumber = frame?.number || Number(new URL(window.location.href).searchParams.get("frame")) || 1
    const globalMs = this.currentGlobalMs()
    return { frameNumber, globalMs }
  }

  indexForMediaSnapshot(snapshot) {
    const byFrame = this.frames.findIndex((frame) => frame.number === snapshot.frameNumber)
    if (byFrame >= 0) return byFrame
    return this.frameIndexForGlobal(snapshot.globalMs || 0)
  }

  restoreMediaSnapshot(snapshot) {
    if (!snapshot) return
    this.setTimelinePosition(snapshot.globalMs || 0, { source: "manifest-refresh" })
  }

  currentGlobalMs() {
    if (this.playingThrough && Number.isFinite(this.playAnchorGlobalMs) && Number.isFinite(this.playAnchorPerf)) {
      const audioGlobalMs = this.dialogueAudioGlobalMs()
      if (Number.isFinite(audioGlobalMs)) {
        this.playAnchorGlobalMs = audioGlobalMs
        this.playAnchorPerf = performance.now()
        return this.clampGlobalMs(audioGlobalMs)
      }
      return this.clampGlobalMs(this.playAnchorGlobalMs + (performance.now() - this.playAnchorPerf) * this.speed)
    }
    if (Number.isFinite(this.pausedGlobalMs)) return this.clampGlobalMs(this.pausedGlobalMs)

    const frameStart = this.frameStartMs?.[this.currentIndex] || 0
    const duration = this.currentFrame?.durationMs || 1
    if (this.lastScrubProgress != null) return this.clampGlobalMs(frameStart + this.lastScrubProgress * duration)
    return this.clampGlobalMs(frameStart)
  }

  dialogueAudioGlobalMs() {
    const event = this.captionAudioEvent
    const audio = event?.audio
    if (!event || (event.trackKey || this.trackKeyForEvent(event)) !== "dialogue") return null
    if (!audio || audio.paused || audio.ended) return null
    if (audio.currentTime <= 0) return event.startMs || 0

    const sourceInMs = Math.max(0, Number(event.sourceInMs || 0))
    const elapsedMs = Math.max(0, (audio.currentTime * 1000) - sourceInMs)
    const durationMs = Math.max(0, Number(event.durationMs || 0))
    return (event.startMs || 0) + (durationMs > 0 ? Math.min(elapsedMs, durationMs) : elapsedMs)
  }

  clampGlobalMs(globalMs) {
    const total = Number(this.totalMs || 0)
    return Math.max(0, Math.min(total, Number(globalMs) || 0))
  }

  currentTimelinePosition() {
    return positionForGlobalMs(this.timeline, this.currentGlobalMs(), this.currentIndex)
  }

  setTimelinePosition(globalMs, { source = "seek", autoplay = false, updateUrl = true } = {}) {
    if (!this.frames?.length) return
    const position = positionForGlobalMs(this.timeline, globalMs, this.currentIndex)
    const previousIndex = this.currentIndex
    this.currentIndex = position.index
    const changedFrame = previousIndex !== this.currentIndex

    if (changedFrame || source === "manifest-refresh" || autoplay) {
      this.showFrame(this.currentIndex, {
        autoplay: autoplay && source !== "scrub",
        lightweight: source === "scrub",
      })
    }

    this.pausedGlobalMs = position.globalMs
    this.lastScrubProgress = position.progress
    this.updatePosition(position.inFrameMs)
    this.updatePlayhead(position.progress)
    this.armAudioEvents(position.globalMs)
    this.syncNoteContext(this.currentFrame)
    this.refreshComposerAnchor({ force: true })
    this.paintCaptionAt(position.inFrameMs)
    if (updateUrl) this.updateUrlFrame()
    this.renderDebugHud()
  }

  mediaUrl(src) {
    if (!src || !this.mediaRefreshToken || /^(data|blob):/i.test(src)) return src
    try {
      const url = new URL(src, window.location.href)
      if (url.origin !== window.location.origin) return src
      url.searchParams.set("_ms_media", this.mediaRefreshToken)
      return url.toString()
    } catch (_) {
      return src
    }
  }

  restoreImageQuality() {
    try {
      const saved = localStorage.getItem(IMAGE_QUALITY_KEY)
      if ([IMAGE_QUALITY_AUTO, IMAGE_QUALITY_MOBILE, IMAGE_QUALITY_FULL].includes(saved)) return saved
    } catch (_) { /* ignore */ }
    return IMAGE_QUALITY_AUTO
  }

  persistImageQuality() {
    try { localStorage.setItem(IMAGE_QUALITY_KEY, this.imageQuality || IMAGE_QUALITY_AUTO) } catch (_) { /* ignore */ }
  }

  syncQualityOptions(qualities = []) {
    this.qualityLabels = {}
    const options = qualities.length ? qualities : [
      { key: IMAGE_QUALITY_AUTO, label: "Auto" },
      { key: IMAGE_QUALITY_MOBILE, label: "540p" },
      { key: IMAGE_QUALITY_FULL, label: "Original" },
    ]
    options.forEach((quality) => { this.qualityLabels[quality.key] = quality.label })
    if (!this.hasQualitySelectTarget) return

    this.qualitySelectTarget.replaceChildren(...options.map((quality) => {
      const option = document.createElement("option")
      option.value = quality.key
      option.textContent = quality.label
      return option
    }))
    this.syncQualitySelect()
  }

  setQuality() {
    const next = this.qualitySelectTarget.value
    if (![IMAGE_QUALITY_AUTO, IMAGE_QUALITY_MOBILE, IMAGE_QUALITY_FULL].includes(next)) return
    this.imageQuality = next
    this.persistImageQuality()
    this.syncQualitySelect()
    this.preloadedImages = new Set()

    const frame = this.currentFrame
    if (frame) this.previewTarget.src = this.mediaUrl(this.frameImageSrc(frame))
    this.preloadNearbyFrames(this.currentIndex)
  }

  syncQualitySelect() {
    if (!this.hasQualitySelectTarget) return

    const auto = Array.from(this.qualitySelectTarget.options).find((option) => option.value === IMAGE_QUALITY_AUTO)
    if (auto) {
      const resolved = this.qualityLabelFor(this.resolvedImageQuality())
      auto.textContent = resolved ? `Auto (${resolved})` : "Auto"
    }
    this.qualitySelectTarget.value = this.imageQuality || IMAGE_QUALITY_AUTO
  }

  resolvedImageQuality() {
    if (this.imageQuality === IMAGE_QUALITY_MOBILE || this.imageQuality === IMAGE_QUALITY_FULL) return this.imageQuality

    const connection = navigator.connection || navigator.webkitConnection || navigator.mozConnection
    if (connection?.saveData) return IMAGE_QUALITY_MOBILE
    if (/^(slow-2g|2g|3g)$/i.test(connection?.effectiveType || "")) return IMAGE_QUALITY_MOBILE

    const ua = navigator.userAgent || ""
    if (/\biPhone\b|\biPod\b/i.test(ua)) return IMAGE_QUALITY_MOBILE
    if (window.matchMedia?.("(pointer: coarse)")?.matches && Math.min(window.innerWidth, window.innerHeight) <= 480) {
      return IMAGE_QUALITY_MOBILE
    }
    return IMAGE_QUALITY_FULL
  }

  frameImageSrc(frame) {
    if (!frame) return null
    const variants = frame.imageVariants || {}
    const quality = this.resolvedImageQuality()
    return variants[quality] || variants.full || frame.image
  }

  qualityLabelFor(key) {
    return this.qualityLabels?.[key] || { [IMAGE_QUALITY_MOBILE]: "540p", [IMAGE_QUALITY_FULL]: "Original" }[key]
  }

  // One <audio> per timeline event, sorted by start and kept off the DOM.
  // The real src is attached lazily as the event fires; eager preload here makes
  // the review page stampede every SFX/music file before the first frame is usable.
  loadAudioEvents(events) {
    this.audioEvents = events
      .map((event, index) => {
        const audio = new Audio()
        audio.preload = "none"
        const item = { ...event, index, audio, loadedSrc: null, trackKey: this.trackKeyForEvent(event) }
        audio.addEventListener("error", () => this.handleAudioEventError(item, "error"))
        audio.addEventListener("stalled", () => this.handleAudioEventError(item, "stalled"))
        return item
      })
      .sort((a, b) => a.startMs - b.startMs)
  }

  handleAudioEventError(event, reason) {
    event.loadedSrc = null
    this.lastMediaError = `${event.trackKey || "audio-event"} ${reason} at ${new Date().toISOString()}`
    this.firedEvents.delete(event.index)
    if (this.playingThrough) {
      this.pendingMediaRefresh = true
      this.renderDebugHud()
      return
    }
    this.refreshManifestMedia({ reason: "audio-event", force: true })
  }

  ensureEventAudio(event) {
    if (!event?.audio || !event.src) return null
    const src = this.mediaUrl(event.src)
    if (event.loadedSrc !== src || event.audio.getAttribute("src") !== src) {
      event.audio.preload = "auto"
      event.audio.src = src
      event.loadedSrc = src
      try { event.audio.load() } catch (_) { /* ignore */ }
    }
    return event.audio
  }

  // Called from the clock with the exact global playhead. Fires any event whose
  // start has just been crossed; a backward seek re-arms events ahead of the head.
  scheduleAudioEvents(globalMs) {
    if (!this.audioEvents.length) return
    this.audioEvents.forEach((event) => {
      const crossed = globalMs >= event.startMs && globalMs < event.startMs + (event.durationMs || 1000) + 250
      if (crossed && !this.firedEvents.has(event.index)) {
        if (this.eventMuted(event)) return
        this.firedEvents.add(event.index)
        this.playAudioEventAt(event, globalMs)
      } else if (globalMs < event.startMs && this.firedEvents.has(event.index)) {
        this.firedEvents.delete(event.index)
      }
    })
  }

  playAudioEventAt(event, globalMs) {
    try {
      const audio = this.ensureEventAudio(event)
      if (!audio) return
      const playToken = (event.playToken || 0) + 1
      event.playToken = playToken
      const offset = this.eventCurrentTime(event, globalMs)

      const seekAndPlay = () => {
        if (event.playToken !== playToken || !this.firedEvents.has(event.index)) return
        audio.volume = this.eventVolume(event)
        audio.muted = this.eventMuted(event)
        audio.playbackRate = this.speed || 1
        try { audio.currentTime = offset } catch (_) { /* ignore */ }
        if ((event.trackKey || this.trackKeyForEvent(event)) === "dialogue") {
          this.captionAudioEvent = event
          this.startKaraoke()
        }
        const attempt = audio.play()
        if (attempt) attempt.catch((error) => {
          if (error?.name === "NotAllowedError") this.blockSoundPlayback()
          else this.handleAudioEventError(event, error?.name || "play")
        })
      }

      if (audio.readyState >= 1) {
        seekAndPlay()
      } else {
        let done = false
        const onReady = () => {
          if (done) return
          done = true
          audio.removeEventListener("loadedmetadata", onReady)
          audio.removeEventListener("canplay", onReady)
          seekAndPlay()
        }
        audio.addEventListener("loadedmetadata", onReady)
        audio.addEventListener("canplay", onReady)
        try { audio.currentTime = offset } catch (_) { /* ignore */ }
      }
    } catch (_) { /* ignore */ }
  }

  // Arm (but do NOT play) every event against a global position. Used while
  // paused (scrubbing the playhead): events whose start is at/behind the head
  // are marked fired so a later Play/resume doesn't double-fire already-past
  // SFX, while events ahead of the head stay un-fired so they sound normally
  // once playback crosses them.
  armAudioEvents(globalMs) {
    if (!this.audioEvents.length) return
    this.audioEvents.forEach((event) => {
      if (globalMs >= event.startMs) this.firedEvents.add(event.index)
      else this.firedEvents.delete(event.index)
    })
  }

  // Silence and re-arm every event (on stop, frame change, or disconnect).
  stopAudioEvents() {
    this.audioEvents.forEach((event) => {
      event.playToken = (event.playToken || 0) + 1
      try { event.audio.pause() } catch (_) { /* ignore */ }
    })
    this.captionAudioEvent = null
    this.stopKaraoke()
    this.firedEvents.clear()
  }

  trackKeyForEvent(event) {
    const kind = event?.trackKind || event?.role
    if (kind === "music") return "music"
    if (kind === "sfx" || kind === "ambient") return "effects"
    return "dialogue"
  }

  restoreAudioTrackMuted() {
    try {
      const parsed = JSON.parse(localStorage.getItem(AUDIO_TRACK_MUTED_KEY) || "{}")
      return {
        dialogue: Boolean(parsed.dialogue),
        music: Boolean(parsed.music),
        effects: Boolean(parsed.effects),
      }
    } catch (_) {
      return { dialogue: false, music: false, effects: false }
    }
  }

  persistAudioTrackMuted() {
    try { localStorage.setItem(AUDIO_TRACK_MUTED_KEY, JSON.stringify(this.audioTrackMuted || {})) } catch (_) { /* ignore */ }
  }

  trackMuted(trackKey) {
    return Boolean(this.audioTrackMuted?.[trackKey])
  }

  masterMuted() {
    return this.audioTarget.muted || this.audioTarget.volume === 0
  }

  eventMuted(event) {
    return this.masterMuted() || this.trackMuted(event.trackKey || this.trackKeyForEvent(event))
  }

  eventVolume(event) {
    const master = this.hasVolumeRangeTarget ? Number(this.volumeRangeTarget.value) : this.audioTarget.volume
    const gain = Number.isFinite(Number(event?.gain)) ? Number(event.gain) : 1
    return Math.min(1, Math.max(0, gain * master))
  }

  eventCurrentTime(event, globalMs) {
    const eventElapsedMs = Math.max(0, globalMs - (event.startMs || 0))
    const sourceInMs = Math.max(0, Number(event.sourceInMs || 0))
    return (sourceInMs + eventElapsedMs) / 1000
  }

  applyAudioEventLevels() {
    this.audioEvents.forEach((event) => {
      if (!event.audio) return
      event.audio.volume = this.eventVolume(event)
      event.audio.muted = this.eventMuted(event)
    })
  }

  stopAudioTrack(trackKey) {
    this.audioEvents.forEach((event) => {
      if ((event.trackKey || this.trackKeyForEvent(event)) !== trackKey) return
      try { event.audio.pause() } catch (_) { /* ignore */ }
    })
  }

  toggleAudioTrack(event) {
    const trackKey = event.params.audioTrack
    if (!trackKey || trackKey === "dialogue") return
    this.audioTrackMuted[trackKey] = !this.trackMuted(trackKey)
    if (this.audioTrackMuted[trackKey]) this.stopAudioTrack(trackKey)
    else this.firedEvents.forEach((idx) => {
      const audioEvent = this.audioEvents.find((ev) => ev.index === idx)
      if ((audioEvent?.trackKey || this.trackKeyForEvent(audioEvent)) === trackKey) this.firedEvents.delete(idx)
    })
    this.persistAudioTrackMuted()
    this.applyAudioEventLevels()
    this.syncAudioTrackButtons()
    if (this.frames?.length) this.renderStrip()
  }

  renderManifestError() {
    if (!this.hasViewerErrorTarget) return
    this.viewerErrorTarget.hidden = false
  }

  retryManifest() {
    this.loadManifest()
  }

  // --- Seek engine ---------------------------------------------------------

  // Single entry point for cells, chips, keys, frame input, rail event.
  // While playing, stay playing from the target; else show + autoplay clip.
  seekTo(frameNumber, { pulse = false } = {}) {
    const index = this.frames.findIndex((frame) => frame.number === frameNumber)
    if (index < 0) return
    this.seekToIndex(index, { pulse })
  }

  seekToIndex(index, { pulse = false } = {}) {
    index = Math.max(0, Math.min(this.frames.length - 1, index))
    if (this.playingThrough) {
      this.seekNonce += 1
      // Re-anchor the play clock to this frame's start and keep rolling.
      this.playAnchorGlobalMs = this.frameStartMs?.[index] || 0
      this.playAnchorPerf = performance.now()
      this.firedEvents.clear()
      this.startedClip = -1
      this.enterPlayFrame(this.frameIndexForGlobal(this.playAnchorGlobalMs), this.playAnchorGlobalMs)
    } else {
      this.setTimelinePosition(this.frameStartMs?.[index] || 0, { source: "seek", autoplay: true })
    }
    if (pulse) this.pulseCell(index)
    this.autoScrollStrip(index, { instant: true })
    this.updateUrlFrame()
  }

  seekFromEvent(event) {
    const frameNumber = Number(event.detail?.frameNumber)
    if (Number.isFinite(frameNumber)) this.seekTo(frameNumber, { pulse: true })
  }

  // strip cell click
  selectCell(event) {
    const frameNumber = Number(event.currentTarget.dataset.frameNumber)
    if (Number.isFinite(frameNumber)) this.seekTo(frameNumber, { pulse: true })
  }

  // Click action: Stimulus passes the event; the delta is on event.params.step.
  step(event) {
    this.stepBy(Number(event?.params?.step))
  }

  // Keyboard + internal call sites pass a plain number.
  stepBy(delta) {
    if (!Number.isFinite(delta)) return
    this.seekToIndex(this.currentIndex + delta, { pulse: true })
  }

  firstFrame() { this.seekToIndex(0, { pulse: true }) }
  lastFrame() { this.seekToIndex(this.frames.length - 1, { pulse: true }) }

  jumpToInput() {
    const value = Number(this.frameInputTarget.value)
    if (Number.isFinite(value)) this.seekTo(value, { pulse: true })
    this.frameInputTarget.blur()
  }

  // Explicit Enter handler so a jump fires immediately, not only on blur/change.
  jumpFromKey(event) {
    event.preventDefault()
    this.jumpToInput()
  }

  initialFrameIndex() {
    const frameNumber = Number(new URL(window.location.href).searchParams.get("frame"))
    if (!Number.isFinite(frameNumber)) return 0
    const index = this.frames.findIndex((frame) => frame.number === frameNumber)
    return index >= 0 ? index : 0
  }

  updateUrlFrame() {
    const frame = this.currentFrame
    if (!frame) return
    const url = new URL(window.location.href)
    url.searchParams.set("frame", frame.number)
    window.history.replaceState({}, "", url)
  }

  // --- Transport -----------------------------------------------------------

  togglePlay() {
    if (this.playingThrough) {
      this.stopPlayback()
      return
    }
    if (!this.frames || !this.frames.length) return
    this.soundBlocked = false
    this.hideBuffering()
    this.abortWaveformDecodes()
    // Browsers only allow audio to start from within a user gesture. The clock
    // drives later plays from rAF (NOT a gesture), so unlock every <audio>
    // element here, inside the click, or real Chrome silences the whole
    // play-through (clip + SFX) while the playhead — being audio-independent —
    // keeps moving, making it look like it's playing with no sound.
    // Anchor a single continuous wall-clock at the current position (honouring a
    // held sub-frame scrub). Play-through is driven ENTIRELY by this clock: the
    // playhead and timecode advance smoothly and frames switch when the clock
    // crosses a boundary, so a clip longer/shorter than its slot — or a slow
    // load — can never stall or rewind the playhead.
    const heldOffset = this.lastScrubProgress != null
      ? this.lastScrubProgress * (this.currentFrame?.durationMs || 0) : 0
    const pausedGlobalMs = Number.isFinite(this.pausedGlobalMs) ? this.pausedGlobalMs : null
    this.playAnchorGlobalMs = pausedGlobalMs ?? ((this.frameStartMs?.[this.currentIndex] || 0) + heldOffset)
    this.currentIndex = this.frameIndexForGlobal(this.playAnchorGlobalMs)
    this.playAnchorPerf = performance.now()
    this.lastScrubProgress = null
    this.pausedGlobalMs = null
    this.unlockAudio(this.playAnchorGlobalMs)
    this.playingThrough = true
    this.syncPlayButton()
    this.firedEvents.clear()
    this.startedClip = -1
    // Timeline events at the exact playhead (notably the F01 birdsong at 0ms)
    // must start inside this same Play gesture. Waiting for the first rAF tick
    // is enough for iOS/Safari to treat the sound as autoplay and drop it.
    this.scheduleAudioEvents(this.playAnchorGlobalMs)
    // Start the current frame's clip IN this gesture (the old engine did this);
    // playTick then continues from the clock and skips re-entering this frame.
    if (this.currentFrame?.audio) {
      this.enterPlayFrame(this.currentIndex, this.playAnchorGlobalMs)
    } else {
      // Silent opening frames are common. Do not pause/clear the audio element
      // immediately after the gesture-time unlock; let the clock enter the first
      // voiced frame and start that clip from an already-primed element.
      this.startedClip = this.currentIndex
    }
    this.startClock()
  }

  // One-time, must run inside a user gesture: "bless" the main clip element and
  // every preloaded SFX/ambient event element with a synchronous muted
  // play()->pause() so the clock can play them later without a gesture. Without
  // this, real Chrome's autoplay policy blocks all audio started from rAF.
  unlockAudio(globalMs = null) {
    if (this._audioUnlocked) return
    this._audioUnlocked = true
    // An <audio> element only unlocks on iOS if a gesture-initiated play() actually
    // STARTS. At the first Play the current frame is often silent (F01), so the main
    // element has no src and the muted bless no-ops — leaving every later
    // clock-driven clip blocked (cold start = no sound + no karaoke; pause→play fixes
    // it because by then the element has a src). Prime the shared element with
    // the next real dialogue clip inside the tap/click; fall back to a tiny
    // silent WAV only if there is no upcoming clip at all.
    const bless = (el, srcOverride = null) => {
      if (!el) return
      try {
        let restore
        if (srcOverride) {
          restore = el.getAttribute("src")
          el.src = srcOverride
          try { el.load() } catch (_) { /* ignore */ }
        }
        const wasMuted = el.muted
        el.muted = true
        const p = el.play()
        const restoreElement = () => {
          const stillBlessingSrc = !srcOverride || el.getAttribute("src") === srcOverride
          const keepActiveMainClip = el === this.audioTarget &&
            stillBlessingSrc &&
            this.playingThrough &&
            this.mediaUrl(this.currentFrame?.audio?.src) === srcOverride
          if (stillBlessingSrc && !keepActiveMainClip) {
            try { el.pause() } catch (_) { /* ignore */ }
            try { el.currentTime = 0 } catch (_) { /* ignore */ }
          }
          el.muted = wasMuted
          if (srcOverride && stillBlessingSrc && !keepActiveMainClip) { restore ? (el.src = restore) : el.removeAttribute("src") }
        }
        if (p && p.then) p.then(restoreElement).catch(restoreElement)
        else window.setTimeout(restoreElement, 0)
      } catch (e) { /* ignore */ }
    }
    this.unlockMainAudioMuted = this.audioTarget.muted
    this.unlockMainAudioSrc = this.nextAudioSrc() || SILENT_WAV
    bless(this.audioTarget, this.unlockMainAudioSrc)
    // Unlock SFX/ambient/music elements using the tiny in-memory WAV, not their
    // real network srcs. That preserves mobile gesture permission without
    // preloading the whole timeline. Even events currently under the playhead are
    // blessed here: MP3 seeks may need to wait for metadata before the real play.
    this.audioEvents.forEach((ev) => {
      bless(ev.audio, SILENT_WAV)
    })
  }

  nextAudioSrc() {
    const frames = this.frames || []
    for (let i = this.currentIndex; i < frames.length; i += 1) {
      const src = frames[i]?.audio?.src
      if (src) return this.mediaUrl(src)
    }
    return null
  }

  showBuffering() {
    if (this.hasLoadingLabelTarget) this.loadingLabelTarget.textContent = "Loading audio..."
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.hidden = false
  }

  showMediaRefreshing() {
    if (this.hasLoadingLabelTarget) this.loadingLabelTarget.textContent = "Refreshing media..."
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.hidden = false
  }

  showSoundBlocked() {
    this.soundBlocked = true
    if (this.hasLoadingLabelTarget) this.loadingLabelTarget.textContent = "Tap Play to enable sound"
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.hidden = false
  }

  blockSoundPlayback() {
    this.pausedGlobalMs = this.currentGlobalMs()
    this.lastMediaError = `audio blocked at ${new Date().toISOString()}`
    this._audioUnlocked = false
    this.unlockMainAudioSrc = null
    this.unlockMainAudioMuted = null
    this.playingThrough = false
    this.syncPlayButton()
    this.stopClock()
    this.stopAudioEvents()
    this.showSoundBlocked()
    this.renderDebugHud()
  }

  hideBuffering() {
    if (this.soundBlocked) return
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.hidden = true
  }

  // One rAF tick of clock-driven play-through. Maps the wall-clock to the global
  // timeline, switches frames at boundaries, and drives the playhead + timecode
  // + SFX scheduler from the same value. Never waits on audio.
  playTick() {
    const total = this.totalMs || 0
    const globalMs = this.currentGlobalMs()
    if (total && globalMs >= total) {
      const last = this.frames.length - 1
      if (last !== this.currentIndex) this.showFrame(last)
      this.updatePlayhead(1)
      this.updatePositionLive(this.currentFrame?.durationMs || 0)
      this.stopPlayback()
      return
    }
    const i = this.frameIndexForGlobal(globalMs)
    if (i !== this.currentIndex || this.startedClip !== i) this.enterPlayFrame(i, globalMs)
    const inFrameMs = globalMs - (this.frameStartMs[i] || 0)
    this.updatePlayhead(inFrameMs / (this.currentFrame?.durationMs || 1))
    this.updatePositionLive(inFrameMs)
    this.paintCaptionAt(inFrameMs)
    this.refreshComposerAnchor()
    this.renderDebugHud()
  }

  // Switch to frame i (load its image + clip) and start that clip alongside the
  // clock. The clip plays best-effort; it never gates the playhead.
  enterPlayFrame(i, globalMs = null) {
    this.startedClip = i
    if (i !== this.currentIndex) this.showFrame(i)
    if (this.currentFrame?.audio) {
      try { this.audioTarget.currentTime = this.clipOffsetSecondsForFrame(i, globalMs) } catch (e) { /* ignore */ }
      if (this.unlockMainAudioSrc === this.mediaUrl(this.currentFrame.audio.src) && typeof this.unlockMainAudioMuted === "boolean") {
        this.audioTarget.muted = this.unlockMainAudioMuted
        this.unlockMainAudioSrc = null
        this.unlockMainAudioMuted = null
      }
      this.playCurrentAudio()
    } else {
      this.audioTarget.pause()
    }
  }

  clipOffsetSecondsForFrame(index, globalMs = null) {
    if (!Number.isFinite(globalMs)) return 0
    const frame = this.frames?.[index]
    if (!frame) return 0
    const start = this.frameStartMs?.[index] || 0
    const duration = frame.durationMs || 1
    return Math.max(0, Math.min(duration, globalMs - start)) / 1000
  }

  // Last frame whose global start is <= globalMs (frameStartMs is cumulative).
  frameIndexForGlobal(globalMs) {
    return frameAtGlobalMs(this.timeline, globalMs, this.currentIndex)?.index || 0
  }

  stopPlayback() {
    if (this.playingThrough) this.pausedGlobalMs = this.currentGlobalMs()
    this.playingThrough = false
    this.seekNonce += 1
    this.syncPlayButton()
    this.audioTarget.pause()
    window.clearTimeout(this.advanceTimer)
    this.advanceTimer = null
    this.stopClock()
    this.stopAudioEvents()
    this.hideBuffering()
    if (this.pendingMediaRefresh) {
      this.pendingMediaRefresh = false
      this.refreshManifestMedia({ reason: "post-playback", force: true })
    }
  }

  playCurrentAudio() {
    // Stamp this clip with the current seek nonce so a late `ended` firing
    // after we've seeked elsewhere is recognised as stale and ignored.
    this.playingNonce = this.seekNonce
    // If the clip isn't buffered enough to start, flag buffering now; the
    // `playing`/`canplay` listeners clear it once audio is actually flowing.
    if (this.playingThrough && this.audioTarget.readyState < 3) this.showBuffering()
    const attempt = this.audioTarget.play()
    if (attempt) attempt.catch((error) => {
      if (error?.name === "NotAllowedError") this.blockSoundPlayback()
      else {
        this.lastMediaError = `audio-play ${error?.name || "error"} at ${new Date().toISOString()}`
        if (this.playingThrough) this.pendingMediaRefresh = true
        else this.refreshManifestMedia({ reason: "audio-play", force: true })
      }
    })
  }

  syncPlayButton() {
    if (!this.hasPlayButtonTarget) return
    this.playButtonTarget.dataset.playing = this.playingThrough ? "true" : "false"
    this.playButtonTarget.innerHTML = this.playIconHtml(this.playingThrough)
  }

  replay() {
    const frame = this.currentFrame
    if (!frame?.audio) return
    this.audioTarget.currentTime = 0
    this.frameStartedAt = performance.now()
    this.playCurrentAudio()
  }

  setSpeed() {
    this.speed = Number(this.speedSelectTarget.value) || 1
    this.audioTarget.defaultPlaybackRate = this.speed
    this.audioTarget.playbackRate = this.speed
    this.audioEvents.forEach((event) => {
      if (event.audio) event.audio.playbackRate = this.speed
    })
  }

  setVolume() {
    const value = Number(this.volumeRangeTarget.value)
    this.audioTarget.volume = value
    if (value > 0) this.audioTarget.muted = false
    this.applyAudioEventLevels()
    this.syncMuteIcon()
  }

  toggleMute() {
    this.audioTarget.muted = !this.audioTarget.muted
    this.applyAudioEventLevels()
    this.syncMuteIcon()
  }

  syncMuteIcon() {
    if (!this.hasMuteIconTarget) return
    const off = this.audioTarget.muted || this.audioTarget.volume === 0
    this.muteIconTarget.innerHTML = off ? this.silentIconHtml() : this.audioIconHtml()
    if (this.hasMuteButtonTarget) this.muteButtonTarget.style.color = off ? "var(--ms-rose)" : "var(--ms-ink)"
  }

  // prev/next frame with an open comment marker
  markerJump(dir) {
    const frames = Object.keys(this.markers)
      .filter((n) => this.markers[n].open > 0)
      .map(Number)
      .sort((a, b) => a - b)
    if (!frames.length) return
    const current = this.currentFrame?.number ?? 0
    const next = dir > 0 ? frames.find((n) => n > current) : [...frames].reverse().find((n) => n < current)
    if (next != null) this.seekTo(next, { pulse: true })
  }

  prevMarker() { this.markerJump(-1) }
  nextMarker() { this.markerJump(1) }

  changeJump(dir) {
    const frames = Array.from(this.changedFrames || []).sort((a, b) => a - b)
    if (!frames.length) return
    const current = this.currentFrame?.number ?? 0
    const next = dir > 0 ? frames.find((n) => n > current) : [...frames].reverse().find((n) => n < current)
    this.seekTo(next ?? (dir > 0 ? frames[0] : frames[frames.length - 1]), { pulse: true })
  }

  prevChange() { this.changeJump(-1) }
  nextChange() { this.changeJump(1) }

  changeCompareVersion() {
    const snapshot = this.mediaSnapshot()
    const compareVersion = this.selectedCompareVersion()
    const url = new URL(window.location.href)
    if (compareVersion) url.searchParams.set("compare_version", compareVersion)
    else url.searchParams.delete("compare_version")
    window.history.replaceState({}, "", url.toString())
    this.loadManifest({ refresh: true, snapshot })
  }

  // --- Zoom ----------------------------------------------------------------

  setZoom() {
    this.stripZoomValue = Number(this.zoomRangeTarget.value)
  }

  fitZoom() {
    this.stripZoomValue = 1
    if (this.hasZoomRangeTarget) this.zoomRangeTarget.value = 1
  }

  stripZoomValueChanged() {
    if (this.hasZoomLabelTarget) this.zoomLabelTarget.textContent = `${Math.round(this.stripZoomValue * 100)}%`
    // value-changed callbacks fire before connect() — guard until frames load.
    if (this.frames?.length) this.renderStrip()
  }

  // --- Layout resizers (B1 rail width/collapse · B2 strip height ·
  //     B3 viewer/top-pane height) ------------------------------------------
  //
  // Three independent dividers, deliberately non-overlapping:
  //   • RAIL resizer  — vertical bar on the rail's left edge; trades stage
  //     width ↔ rail width (B1).
  //   • VIEWER resizer — horizontal divider between the viewer-frame and the
  //     timeline; trades viewer HEIGHT ↔ the lower controls' height (B3). It
  //     adjusts the viewer pane's flex-grow ratio.
  //   • STRIP resizer  — horizontal handle at the strip's bottom; changes
  //     --strip-h, i.e. how TALL the thumbnails / clip lane render (B2). This
  //     is content sizing inside the timeline, not the viewer↔controls split,
  //     so B2 and B3 never fight: B3 owns the pane split, B2 owns strip density.

  restoreLayout() {
    // Rail width
    try {
      const w = Number(localStorage.getItem(RAIL_WIDTH_KEY))
      if (Number.isFinite(w) && w > 0) this.applyRailWidth(w)
    } catch (e) { /* ignore */ }
    // Rail collapsed
    let collapsed = false
    try { collapsed = localStorage.getItem(RAIL_COLLAPSED_KEY) === "true" } catch (e) { /* ignore */ }
    this.applyRailCollapsed(collapsed)
    // Strip height
    try {
      const h = Number(localStorage.getItem(STRIP_H_KEY))
      if (Number.isFinite(h) && h > 0) this.stripHeight = this.clampStripH(h)
    } catch (e) { /* ignore */ }
    // Viewer pane height. Additionally clamp to the current viewport so a tall
    // value saved on a big monitor doesn't push the transport + composer below
    // the fold when restored on a shorter laptop screen.
    try {
      const h = Number(localStorage.getItem(VIEWER_H_KEY))
      if (Number.isFinite(h) && h > 0) {
        const fitMax = Math.max(VIEWER_H_MIN, (this.element.clientHeight || 0) - 220)
        this.applyViewerHeight(Math.min(h, fitMax))
      }
    } catch (e) { /* ignore */ }
  }

  // Clear ALL persisted workspace state and snap the layout back to defaults
  // live (no reload). Covers this controller's keys (rail width/collapse, strip
  // height, viewer pane) and dispatches to the header (text size) + rail
  // (filters) controllers so they reset their own persisted state too.
  resetWorkspace() {
    [RAIL_WIDTH_KEY, RAIL_COLLAPSED_KEY, STRIP_H_KEY, VIEWER_H_KEY].forEach((key) => {
      try { localStorage.removeItem(key) } catch (e) { /* ignore */ }
    })

    // Rail back to default width + expanded.
    this.applyRailWidth(RAIL_DEFAULT)
    this.applyRailCollapsed(false)

    // Strip height back to default.
    this.stripHeight = STRIP_H_DEFAULT

    // Viewer pane: drop the pinned basis so it returns to the flex-grow default.
    this.viewerHeight = null
    if (this.hasViewerFrameTarget) this.viewerFrameTarget.style.flex = ""

    // Tell the sibling controllers to reset their own persisted state.
    this.dispatch("reset-workspace", { prefix: "animatic", target: window, bubbles: true })

    // Re-render the strip at the default height + re-place the playhead.
    if (this.frames?.length) {
      window.requestAnimationFrame(() => {
        this.renderStrip()
        this.paintStripCurrent()
        // While paused, currentProgress() saturates to 1 (wall-clock fallback
        // keeps growing), which would paint the playhead at the frame's right
        // edge and clobber a held sub-frame scrub. Repaint from the held
        // position instead; only trust the live clock while actually playing.
        this.updatePlayhead(this.playingThrough ? this.currentProgress() : (this.lastScrubProgress ?? 0))
      })
    }
  }

  // --- B1: rail width + collapse -------------------------------------------

  clampRailWidth(w) { return Math.max(RAIL_MIN, Math.min(RAIL_MAX, Math.round(w))) }

  applyRailWidth(w) {
    const width = this.clampRailWidth(w)
    this.railWidth = width
    if (this.hasRailTarget) {
      this.railTarget.style.flex = `0 0 ${width}px`
      this.railTarget.style.width = `${width}px`
    }
  }

  startRailResize(event) {
    if (this.railCollapsed) return
    event.preventDefault()
    this.railResizeActive = true
    const onMove = (e) => {
      // Rail is on the right; dragging left (smaller clientX) widens it.
      const right = this.element.getBoundingClientRect().right
      this.applyRailWidth(right - e.clientX)
    }
    const onUp = () => {
      this.railResizeActive = false
      try { localStorage.setItem(RAIL_WIDTH_KEY, String(this.railWidth || RAIL_DEFAULT)) } catch (e) { /* ignore */ }
      this.teardownLayoutResize()
    }
    this.beginLayoutResize(event, onMove, onUp)
  }

  railResizeKey(event) {
    const step = event.shiftKey ? 40 : 16
    if (event.key === "ArrowLeft") { event.preventDefault(); this.applyRailWidth((this.railWidth || RAIL_DEFAULT) + step) }
    else if (event.key === "ArrowRight") { event.preventDefault(); this.applyRailWidth((this.railWidth || RAIL_DEFAULT) - step) }
    else return
    try { localStorage.setItem(RAIL_WIDTH_KEY, String(this.railWidth || RAIL_DEFAULT)) } catch (e) { /* ignore */ }
  }

  // Collapse to a slim VISIBLE strip (~44px) rather than hiding the rail. We
  // toggle an `is-collapsed` class on the rail (and the root, so the stage can
  // reclaim the freed width via CSS); the reopen toggle in the railhead stays
  // rendered and clickable inside that strip. We never set `hidden`/display:none
  // on the rail itself, so a persisted `rail-collapsed=true` always loads as a
  // recoverable strip with a visible toggle — never a blank hidden rail.
  applyRailCollapsed(collapsed) {
    this.railCollapsed = collapsed
    if (this.hasRailTarget) this.railTarget.classList.toggle("is-collapsed", collapsed)
    this.element.classList.toggle("is-rail-collapsed", collapsed)
    // The drag-resize handle is meaningless while collapsed (fixed-width strip),
    // so hide it; the reopen toggle in the railhead drives expand instead.
    if (this.hasRailResizerTarget) this.railResizerTarget.hidden = collapsed
    if (this.hasRailCollapseTarget) {
      this.railCollapseTarget.setAttribute("aria-expanded", String(!collapsed))
    }
    if (this.hasRailHeaderToggleTarget) {
      this.railHeaderToggleTarget.classList.toggle("is-collapsed", collapsed)
      this.railHeaderToggleTarget.setAttribute("aria-expanded", String(!collapsed))
    }
  }

  toggleRail() {
    this.applyRailCollapsed(!this.railCollapsed)
    try { localStorage.setItem(RAIL_COLLAPSED_KEY, String(this.railCollapsed)) } catch (e) { /* ignore */ }
    // The strip width depends on the stage width; re-render after the layout
    // reflows so the playhead/cells track the new stage size.
    window.requestAnimationFrame(() => { if (this.frames?.length) { this.renderStrip(); this.paintStripCurrent() } })
  }

  // --- B2: strip height ----------------------------------------------------

  clampStripH(h) { return Math.max(STRIP_H_MIN, Math.min(STRIP_H_MAX, Math.round(h))) }

  startStripResize(event) {
    event.preventDefault()
    const startY = event.clientY
    const startH = this.stripHeight || STRIP_H_DEFAULT
    const onMove = (e) => {
      // Drag DOWN (greater clientY) makes the strip TALLER, per the handoff.
      this.stripHeight = this.clampStripH(startH + (e.clientY - startY))
      this.renderStrip()
      this.paintStripCurrent()
      // Paused: paint from the held scrub position, not the wall-clock-saturated
      // currentProgress() (see resetWorkspace). Only the live clock while playing.
      this.updatePlayhead(this.playingThrough ? this.currentProgress() : (this.lastScrubProgress ?? 0))
    }
    const onUp = () => {
      try { localStorage.setItem(STRIP_H_KEY, String(this.stripHeight)) } catch (e) { /* ignore */ }
      this.teardownLayoutResize()
    }
    this.beginLayoutResize(event, onMove, onUp)
  }

  // --- B3: viewer / top-pane height ----------------------------------------

  clampViewerH(h) { return Math.max(VIEWER_H_MIN, Math.min(VIEWER_H_MAX, Math.round(h))) }

  applyViewerHeight(h) {
    this.viewerHeight = this.clampViewerH(h)
    // Pin an explicit basis so the viewer keeps its dragged height even when
    // the column overflows (the stage scrolls). flex-grow 0 stops it reclaiming
    // free space and fighting the user's chosen size.
    if (this.hasViewerFrameTarget) {
      this.viewerFrameTarget.style.flex = `0 0 ${this.viewerHeight}px`
    }
  }

  startViewerResize(event) {
    event.preventDefault()
    const frame = this.hasViewerFrameTarget ? this.viewerFrameTarget : null
    if (!frame) return
    const startY = event.clientY
    const startPx = frame.getBoundingClientRect().height
    const onMove = (e) => {
      // Drag DOWN (greater clientY) makes the viewer TALLER.
      this.applyViewerHeight(startPx + (e.clientY - startY))
    }
    const onUp = () => {
      try { localStorage.setItem(VIEWER_H_KEY, String(this.viewerHeight)) } catch (e) { /* ignore */ }
      this.teardownLayoutResize()
    }
    this.beginLayoutResize(event, onMove, onUp)
  }

  // Shared lifecycle for the three layout resizers. Captures the pointer on the
  // handle so the matching `up` always fires there (even if released outside the
  // window), listens for `pointercancel` too, and records teardown state so an
  // in-flight drag can be torn down cleanly in disconnect().
  beginLayoutResize(event, onMove, onUp) {
    const handle = event.target
    const pointerId = event.pointerId
    try { handle.setPointerCapture?.(pointerId) } catch (e) { /* ignore */ }
    this.layoutResize = { onMove, onUp, handle, pointerId }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
    document.body.style.userSelect = "none"
  }

  teardownLayoutResize() {
    const r = this.layoutResize
    if (!r) return
    window.removeEventListener("pointermove", r.onMove)
    window.removeEventListener("pointerup", r.onUp)
    window.removeEventListener("pointercancel", r.onUp)
    try { r.handle?.releasePointerCapture?.(r.pointerId) } catch (e) { /* ignore */ }
    document.body.style.userSelect = ""
    this.layoutResize = null
  }

  // --- showFrame -----------------------------------------------------------

  showFrame(index, { autoplay = false, lightweight = false } = {}) {
    // A manual jump resets the SFX/clip scheduler; during play-through we leave
    // events alone so a clip that straddles a cut (e.g. a held music bed) keeps
    // playing across frames.
    if (!this.playingThrough && !lightweight) this.stopAudioEvents()
    this.currentIndex = index
    const frame = this.currentFrame
    if (!frame) return

    this.previewTarget.src = this.mediaUrl(this.frameImageSrc(frame))
    this.previewTarget.hidden = false
    this.previewTarget.alt = `${frame.label} storyboard frame`
    this.frameLabelTargets.forEach((el) => { el.textContent = frame.label })
    this.syncSequenceLabel(frame)
    if (this.hasAudioLabelTarget) {
      this.audioLabelTarget.textContent = frame.audio?.filename || "silent"
      this.audioLabelTarget.classList.toggle("is-silent", !frame.audio)
    }
    this.syncNoteContext(frame)
    this.syncFrameSourceContext(frame)
    if (this.hasReplayButtonTarget) this.replayButtonTarget.disabled = !frame.audio

    if (!lightweight) {
      this.audioTarget.pause()
      this.audioTarget.removeAttribute("src")

      if (frame.audio) {
        this.audioTarget.src = this.mediaUrl(frame.audio.src)
        this.audioTarget.load()
        // load() resets playbackRate to 1 — re-apply the chosen speed.
        this.audioTarget.defaultPlaybackRate = this.speed
        this.audioTarget.playbackRate = this.speed
      } else {
        this.warmUpcomingAudio(index)
      }
    }

    this.renderCaption(frame)
    this.preloadNearbyFrames(index)
    this.frameStartedAt = performance.now()
    if (!lightweight) this.updatePosition(0)
    this.paintStripCurrent()
    if (!lightweight) this.updatePlayhead(0)

    this.dispatch("frame-changed", { detail: { frameNumber: frame.number, timeMs: this.frameStartMs[index] || 0 }, prefix: "animatic", target: window, bubbles: true })

    if (this.hasFrameInputTarget && document.activeElement !== this.frameInputTarget) {
      this.frameInputTarget.value = String(frame.number).padStart(2, "0")
    }

    if (autoplay && frame.audio && !lightweight) this.playCurrentAudio()
  }

  preloadNearbyFrames(index) {
    // Warm only the next couple of main-frame images. The strip uses lightweight
    // thumbnails, so this prefetch window is reserved for playback-sized frames.
    for (let i = index + 1; i <= Math.min(this.frames.length - 1, index + 2); i += 1) {
      const src = this.frameImageSrc(this.frames[i])
      const mediaSrc = this.mediaUrl(src)
      if (!mediaSrc || this.preloadedImages.has(mediaSrc)) continue
      this.preloadedImages.add(mediaSrc)
      const img = new Image()
      img.decoding = "async"
      img.src = mediaSrc
    }
  }

  warmUpcomingAudio(index) {
    // Keep the forward audio window tiny. The current clip is loaded on the real
    // player element; this only nudges the next voiced frame into browser cache.
    const frames = this.frames || []
    for (let i = index + 1; i < frames.length; i += 1) {
      const src = frames[i]?.audio?.src
      const mediaSrc = this.mediaUrl(src)
      if (!mediaSrc) continue
      if (this.warmedAudio.has(mediaSrc)) return
      const audio = new Audio()
      audio.preload = "auto"
      audio.src = mediaSrc
      try { audio.load() } catch (e) { /* ignore */ }
      this.warmedAudio.set(mediaSrc, audio)
      return
    }
  }

  syncSequenceLabel(frame) {
    if (!this.hasSequenceLabelTarget) return
    const sequence = frame.sequence
    this.sequenceLabelTarget.textContent = sequence ? `SEQ ${sequence.id} · ${sequence.name}` : ""
    this.sequenceLabelTarget.hidden = !sequence
  }

  // --- Strip rendering -----------------------------------------------------

  renderStrip() {
    if (!this.hasStripInnerTarget) return
    const zoom = this.stripZoomValue
    const stripH = this.stripHeight || STRIP_H_DEFAULT
    const min = Math.round(STRIP.min * zoom)
    const max = Math.round(STRIP.max * zoom)
    const label = Math.round(STRIP.label * Math.min(1.1, zoom))

    const durations = this.frames.map((f) => f.durationMs || 0)
    const maxDur = Math.max(...durations, 1)
    const minDur = Math.min(...durations, 0)
    this.cellWidths = durations.map((d) => {
      const t = (d - minDur) / Math.max(1, maxDur - minDur)
      return Math.round(min + t * (max - min))
    })

    this.cellOffsets = []
    let x = TIMELINE_GUTTER
    this.cellWidths.forEach((w) => { this.cellOffsets.push(x); x += w + STRIP.gap })
    this.innerWidth = (this.cellOffsets[this.cellOffsets.length - 1] || TIMELINE_GUTTER) + (this.cellWidths[this.cellWidths.length - 1] || 0) + STRIP.padX

    const dialogueClips = this.buildDialogueClips()

    const cellsHtml = this.frames.map((frame, i) => {
      const w = this.cellWidths[i]
      const showLabel = w >= label
      const silent = !frame.audio
      const leadingMargin = i === 0 ? `margin-left:${Math.max(0, TIMELINE_GUTTER - STRIP.padX)}px;` : ""
      // background-image is set safely via DOM after insertion (see below) so a
      // url with quotes/parens can't terminate the CSS url(...) token.
      return `
        <button class="ms-studio__cell" type="button" data-frame-number="${frame.number}"
          data-action="animatic-review#selectCell" style="${leadingMargin}width:${w}px;height:${stripH}px"
          aria-label="Show ${this.escapeHtml(frame.label)}">
          <span class="ms-studio__cell-scrim"></span>
          ${showLabel ? `<span class="ms-mono ms-studio__cell-label">${this.escapeHtml(frame.label)}</span>` : ""}
          <span class="ms-studio__cell-fill"></span>
          <span class="ms-studio__change-marker" hidden></span>
          <span class="ms-studio__marker" hidden></span>
          <span class="ms-studio__audio-pill ${silent ? "is-silent" : ""}"></span>
        </button>`
    }).join("")

    const audioLanesHtml = AUDIO_TRACKS.map((track) => {
      const clips = track.key === "dialogue" ? dialogueClips : this.buildAudioEventClips(track.key)
      return this.renderAudioLane(track, clips)
    }).join("")

    // Strip content height (B2) drives cell + clip-lane geometry via --strip-h.
    this.stripInnerTarget.style.setProperty("--strip-h", `${stripH}px`)
    this.stripInnerTarget.style.width = `${this.innerWidth}px`
    const timelineWidth = Math.max(0, this.innerWidth - TIMELINE_GUTTER - STRIP.padX)
    this.stripInnerTarget.innerHTML = `
      <div class="ms-studio__scrub-track" style="left:${TIMELINE_GUTTER}px;width:${timelineWidth}px" data-action="pointerdown->animatic-review#startScrub"></div>
      <div class="ms-studio__rail-line" style="left:${TIMELINE_GUTTER}px;width:${timelineWidth}px"></div>
      ${cellsHtml}
      <div class="ms-studio__audio-lanes">
        ${audioLanesHtml}
      </div>
      <div class="ms-studio__playhead" data-animatic-review-target="playhead">
        <div class="ms-studio__playhead-line"></div>
        <div class="ms-studio__playhead-tri"></div>
        <div class="ms-studio__knob" data-action="pointerdown->animatic-review#startScrub"><span></span><span></span><span></span></div>
      </div>`

    this.cellEls = Array.from(this.stripInnerTarget.querySelectorAll(".ms-studio__cell"))
    // Lazy-load thumbnails. Setting all 55 full-size backgrounds at once saturates
    // the connection and STARVES the playback audio — the cold-start "no sound /
    // karaoke frozen until every screenshot has loaded" bug. Load each cell's
    // thumbnail only as it nears the strip viewport, with the URL encoded safely.
    this.thumbObserver?.disconnect()
    this.thumbObserver = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const cell = entry.target
        const image = cell.dataset.thumbSrc
        if (image) {
          cell.style.backgroundImage = `url("${encodeURI(image).replace(/"/g, "%22")}")`
          cell.removeAttribute("data-thumb-src")
        }
        obs.unobserve(cell)
      })
    }, { root: this.hasStripTarget ? this.stripTarget : null, rootMargin: "300px" })
    this.cellEls.forEach((cell, i) => {
      const image = this.mediaUrl(this.frames[i]?.thumbnailImage || this.frames[i]?.image)
      if (image) { cell.dataset.thumbSrc = image; this.thumbObserver.observe(cell) }
    })
    this.playheadEl = this.stripInnerTarget.querySelector(".ms-studio__playhead")
    this.renderChangeMarkers()
    this.renderMarkers()
    this.paintStripCurrent()
    this.updatePlayhead(0)
    // Waveform decode downloads each clip IN FULL, so defer it (idle) and pause it
    // during playback (see queueWaveformDecode) — it must never compete with the
    // playback audio for the connection.
    this.scheduleWaveforms()
  }

  renderAudioLane(track, clips) {
    const muted = this.trackMuted(track.key)
    const canMute = track.key !== "dialogue"
    const label = this.escapeHtml(track.label)
    const laneHead = canMute
      ? `<div class="ms-studio__audio-lane-head">
          <button class="ms-studio__track-mute ${muted ? "is-muted" : ""}" type="button"
            data-action="animatic-review#toggleAudioTrack"
            data-animatic-review-audio-track-param="${track.key}"
            data-track-label="${label}"
            aria-label="${muted ? "Unmute" : "Mute"} ${label}"
            aria-pressed="${muted ? "true" : "false"}">${this.speakerIconHtml(muted)}</button>
          <span class="ms-mono ms-studio__clip-lane-label">${label}</span>
        </div>`
      : `<div class="ms-studio__audio-lane-head">
          <span class="ms-studio__track-spacer" aria-hidden="true"></span>
          <span class="ms-mono ms-studio__clip-lane-label">${label}</span>
        </div>`

    const clipsHtml = clips.map((clip) => {
      const src = clip.src ? ` data-clip-src="${this.escapeHtml(clip.src)}"` : ""
      const title = `${track.label}: ${clip.file}`
      return `<div class="ms-studio__clip is-${track.key} ${clip.src ? "" : "is-plain"}" title="${this.escapeHtml(title)}"${src} style="left:${clip.left}px;width:${clip.width}px">
        <canvas class="ms-studio__clip-wave"></canvas>
        <span class="ms-mono ms-studio__clip-file">${this.escapeHtml(clip.file)}</span>
      </div>`
    }).join("")

    return `<div class="ms-studio__audio-lane is-${track.key}">
      ${laneHead}
      ${clipsHtml || `<span class="ms-studio__audio-lane-empty">No ${label}</span>`}
    </div>`
  }

  syncAudioTrackButtons() {
    this.stripInnerTarget?.querySelectorAll("[data-animatic-review-audio-track-param]").forEach((button) => {
      const key = button.dataset.animaticReviewAudioTrackParam
      const label = button.dataset.trackLabel || button.textContent.trim()
      const muted = this.trackMuted(key)
      button.classList.toggle("is-muted", muted)
      button.setAttribute("aria-pressed", String(muted))
      button.setAttribute("aria-label", `${muted ? "Unmute" : "Mute"} ${label}`)
      button.innerHTML = this.speakerIconHtml(muted)
    })
  }

  speakerIconHtml(muted) {
    const slash = muted ? `<path d="M19 5L5 19" />` : `<path d="M15.5 8.5a5 5 0 0 1 0 7" />`
    return `<svg class="ms-studio__track-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M11 5L6 9H3v6h3l5 4z" />
      ${slash}
    </svg>`
  }

  buildAudioEventClips(trackKey) {
    return this.audioEvents
      .filter((event) => (event.trackKey || this.trackKeyForEvent(event)) === trackKey)
      .map((event) => {
        const left = this.xForGlobalMs(event.startMs || 0)
        const right = this.xForGlobalMs((event.startMs || 0) + (event.durationMs || 0))
        return {
          file: event.label || event.trackKind || event.role || "audio event",
          src: this.mediaUrl(event.waveformSrc || event.src || null),
          left,
          width: Math.max(20, right - left),
        }
      })
  }

  buildDialogueClips() {
    const clips = [ ...this.buildAudioEventClips("dialogue"), ...this.buildClips() ]
    const seen = new Set()
    return clips
      .filter((clip) => {
        const key = [
          Math.round(clip.left || 0),
          Math.round(clip.width || 0),
          clip.src || "",
          clip.file || "",
        ].join("|")
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .sort((a, b) => (a.left || 0) - (b.left || 0))
  }

  xForGlobalMs(globalMs) {
    if (!this.frames?.length || !this.cellOffsets?.length) return TIMELINE_GUTTER
    const ms = Math.max(0, Number(globalMs) || 0)
    const last = this.frames.length - 1
    for (let i = 0; i < this.frames.length; i += 1) {
      const start = this.frameStartMs[i] || 0
      const duration = this.frames[i].durationMs || 1
      const end = start + duration
      if (ms <= end || i === last) {
        const progress = Math.max(0, Math.min(1, (ms - start) / duration))
        return (this.cellOffsets[i] || TIMELINE_GUTTER) + progress * (this.cellWidths[i] || 1)
      }
    }
    return (this.cellOffsets[last] || TIMELINE_GUTTER) + (this.cellWidths[last] || 1)
  }

  scheduleWaveforms() {
    this.cancelScheduledWaveforms()
    const run = () => {
      if (this.playingThrough) { this.scheduleWaveforms(); return }
      this.drawAllWaveforms()
    }
    const idle = () => {
      this.waveformTimerId = null
      if (window.requestIdleCallback) this.waveformIdleId = window.requestIdleCallback(run, { timeout: 9000 })
      else run()
    }
    this.waveformTimerId = window.setTimeout(idle, 6000)
  }

  cancelScheduledWaveforms() {
    if (this.waveformTimerId) {
      window.clearTimeout(this.waveformTimerId)
      this.waveformTimerId = null
    }
    if (this.waveformIdleId && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.waveformIdleId)
      this.waveformIdleId = null
    }
  }

  // consecutive voiced frames = one clip (buildClips from main.jsx).
  buildClips() {
    const clips = []
    let i = 0
    while (i < this.frames.length) {
      if (!this.frames[i].audio) { i++; continue }
      let j = i
      while (j + 1 < this.frames.length && this.frames[j + 1].audio) j++
      clips.push({
        file: this.frames[i].audio?.filename || "scratch.mp3",
        // First voiced frame's audio drives the real-waveform render (C1).
        // A clip can span several voiced frames; we draw the leading frame's
        // own audio for that block (acceptable per the brief). Prefer the
        // same-origin proxy URL (CORS-clean for Web Audio decode); fall back to
        // the plain src if no proxy URL is available.
        src: this.mediaUrl(this.frames[i].audio?.waveformSrc || this.frames[i].audio?.src || null),
        left: this.cellOffsets[i],
        width: Math.max(20, (this.cellOffsets[j] + this.cellWidths[j]) - this.cellOffsets[i]),
      })
      i = j + 1
    }
    return clips
  }

  renderMarkers() {
    if (!this.cellEls) return
    this.cellEls.forEach((cell) => {
      const frameNumber = Number(cell.dataset.frameNumber)
      const marker = this.markers[frameNumber]
      const badge = cell.querySelector(".ms-studio__marker")
      if (!badge) return
      if (!marker || marker.total === 0) {
        badge.hidden = true
        badge.classList.remove("has-action-dots", "is-resolved")
        badge.textContent = ""
        return
      }
      badge.hidden = false
      const resolved = marker.open === 0
      badge.classList.toggle("is-resolved", resolved)
      const needsAction = marker.noteNeedsAction || 0
      const actioned = marker.noteActioned || 0
      badge.classList.toggle("has-action-dots", needsAction > 0 || actioned > 0)
      if (needsAction > 0 || actioned > 0) {
        badge.style.background = "transparent"
        badge.innerHTML = [
          needsAction > 0 ? `<span class="ms-studio__marker-dot is-needs-action" title="${this.noteCountLabel(needsAction, "needs action", "need action")}">${needsAction > 1 ? needsAction : ""}</span>` : "",
          actioned > 0 ? `<span class="ms-studio__marker-dot is-actioned" title="${this.noteCountLabel(actioned, "has action logged", "have action logged")}">${actioned > 1 ? actioned : ""}</span>` : "",
        ].join("")
      } else {
        badge.style.background = resolved ? "transparent" : (SEV_COLOR[marker.worst] || "var(--ms-faint)")
        badge.textContent = marker.open > 1 ? String(marker.open) : ""
      }
    })
  }

  renderChangeMarkers() {
    if (!this.cellEls) return
    this.cellEls.forEach((cell) => {
      const frameNumber = Number(cell.dataset.frameNumber)
      const marker = cell.querySelector(".ms-studio__change-marker")
      const change = this.frameCompareChange(frameNumber)
      const changed = Boolean(change)
      cell.classList.toggle("has-compare-change", Boolean(changed))
      if (!marker) return
      if (!changed) {
        marker.hidden = true
        marker.textContent = ""
        marker.removeAttribute("title")
        marker.removeAttribute("aria-label")
        delete marker.dataset.tip
        delete marker.dataset.changeSummary
        return
      }
      const details = change?.items?.length ? `: ${change.items.join("; ")}` : ""
      const kinds = this.changeKindsForFrame(frameNumber)
      const summary = `Changed vs ${this.compareVersion || "selected version"}${details || (kinds.length ? `: ${kinds.join(", ")}` : "")}`
      marker.hidden = false
      marker.textContent = "Δ"
      marker.title = summary
      marker.setAttribute("aria-label", summary)
      marker.dataset.tip = summary
      marker.dataset.changeSummary = summary
    })
  }

  changeKindsForFrame(frameNumber) {
    const kinds = []
    if (this.changeSets.image?.has(frameNumber)) kinds.push("image")
    if (this.changeSets.audio?.has(frameNumber)) kinds.push("audio")
    if (this.changeSets.dialogue?.has(frameNumber)) kinds.push("dialogue")
    if (this.changeSets.duration?.has(frameNumber)) kinds.push("timing")
    return kinds
  }

  changeSetsFromDiff(diff = {}) {
    const toSet = (values) => new Set((values || []).map(Number).filter((n) => Number.isFinite(n)))
    const changeFrames = new Set((diff.changes || []).map((change) => Number(change.frameNumber || change.frame_number)).filter((n) => Number.isFinite(n)))
    const frames = Array.isArray(diff.changes) ? changeFrames : toSet(diff.framesChanged || diff.frames_changed)
    return {
      frames,
      image: toSet(diff.imageAssetChanged || diff.image_asset_changed),
      audio: toSet(diff.audioAssetChanged || diff.audio_asset_changed),
      dialogue: toSet(diff.dialogueChanged || diff.dialogue_changed),
      duration: toSet(diff.durationChanged || diff.duration_changed),
    }
  }

  compareChangesFromDiff(diff = {}) {
    const changes = diff.changes || Object.values(diff.frameChanges || diff.frame_changes || {})
    const map = new Map()
    changes.forEach((change) => {
      const frameNumber = Number(change.frameNumber || change.frame_number)
      if (Number.isFinite(frameNumber) && Array.isArray(change.items) && change.items.length) {
        map.set(frameNumber, change)
      }
    })
    return map
  }

  frameCompareChange(frameOrNumber) {
    const frameNumber = typeof frameOrNumber === "number" ? frameOrNumber : Number(frameOrNumber?.number)
    return Number.isFinite(frameNumber) ? this.compareChanges?.get(frameNumber) : null
  }

  selectedCompareVersion() {
    if (this.hasCompareSelectTarget) return this.compareSelectTarget.value || null
    try { return new URL(this.manifestUrlValue, window.location.href).searchParams.get("compare_version") } catch (_) { return null }
  }

  syncCompareControls() {
    if (this.hasCompareSelectTarget && this.compareVersion) {
      const option = Array.from(this.compareSelectTarget.options).find((candidate) => candidate.value === this.compareVersion)
      if (option) this.compareSelectTarget.value = this.compareVersion
    }
    if (this.hasCompareSummaryTarget) {
      const count = this.changedFrames?.size || 0
      this.compareSummaryTarget.textContent = this.compareVersion ? `${count} changed vs ${this.compareVersion}` : `${count} changed`
    }
  }

  noteCountLabel(count, singular, plural) {
    return `${count} note${count === 1 ? "" : "s"} ${count === 1 ? singular : plural}`
  }

  commentsChanged(event) {
    this.markers = event.detail?.markers || {}
    this.renderMarkers()
  }

  paintStripCurrent() {
    if (!this.cellEls) return
    const current = this.currentFrame?.number
    this.cellEls.forEach((cell) => {
      cell.classList.toggle("is-current", Number(cell.dataset.frameNumber) === current)
    })
  }

  pulseCell(index) {
    const frame = this.frames[index]
    if (!frame || !this.cellEls) return
    const cell = this.cellEls.find((el) => Number(el.dataset.frameNumber) === frame.number)
    if (!cell) return
    cell.classList.remove("ms-seek-pulse")
    // reflow to restart the animation
    void cell.offsetWidth
    cell.classList.add("ms-seek-pulse")
    window.setTimeout(() => cell.classList.remove("ms-seek-pulse"), 600)
  }

  scrollInitialFrameIntoView(index) {
    window.requestAnimationFrame(() => this.autoScrollStrip(index, { instant: true }))
  }

  autoScrollStrip(index, { instant = this.playingThrough || this.dragging } = {}) {
    if (this.dragging) return
    if (!this.hasStripTarget || !this.cellEls) return
    const cell = this.cellEls[index]
    const sc = this.stripTarget
    if (!cell || !sc.clientWidth) return
    const cl = cell.offsetLeft
    const cr = cl + cell.offsetWidth
    const vl = sc.scrollLeft
    const vr = vl + sc.clientWidth
    if (cr > vr - 40 || cl < vl + 40) {
      const target = Math.max(0, cl - sc.clientWidth * 0.42)
      if (instant) sc.scrollLeft = target
      else sc.scrollTo({ left: target, behavior: "smooth" })
    }
  }

  // --- Playhead / progress -------------------------------------------------

  // Elapsed ms within the current frame, measured against ONE monotonic source
  // per frame so the strip playhead never jumps backward.
  //
  // The jitter bug: for an audio frame we read audio.currentTime, but while the
  // clip was still LOADING (currentTime === 0) the old code fell through to the
  // wall-clock branch, so the playhead crept forward during the ~30-50ms load
  // latency and then SNAPPED BACK when currentTime took over near zero. Every
  // frame start flickered. Fix: an audio frame is driven ONLY by its own clip
  // clock — hold at 0 until the clip actually starts, track it while playing,
  // and hold at the slot end once it has finished (until we advance). Never mix
  // in the wall-clock. Silent frames use the wall-clock (their only source).
  currentInFrameMs() {
    const frame = this.currentFrame
    if (!frame) return 0
    const cap = frame.durationMs || 1
    const audio = this.audioTarget
    if (frame.audio) {
      if (audio.ended) return cap
      if (!audio.paused && audio.currentTime > 0) {
        return Math.max(0, Math.min(cap, audio.currentTime * 1000))
      }
      return 0
    }
    if (this.frameStartedAt == null) return 0
    const elapsed = (performance.now() - this.frameStartedAt) * this.speed
    return Math.max(0, Math.min(elapsed, cap))
  }

  // 0..1 progress within the current frame, derived from the single in-frame
  // clock so the timecode and the strip playhead never diverge (no jitter).
  currentProgress() {
    const frame = this.currentFrame
    if (!frame) return 0
    return Math.max(0, Math.min(1, this.currentInFrameMs() / (frame.durationMs || 1)))
  }

  updatePlayhead(progress) {
    if (!this.playheadEl || !this.cellOffsets) return
    const i = this.currentIndex
    const x = (this.cellOffsets[i] || STRIP.padX) + progress * (this.cellWidths[i] || 0)
    this.playheadEl.style.left = `${x}px`
    const live = this.playingThrough || this.dragging
    this.playheadEl.classList.toggle("is-live", live)
    const cell = this.cellEls?.[i]
    if (cell) {
      const fill = cell.querySelector(".ms-studio__cell-fill")
      if (fill) {
        fill.style.width = `${progress * 100}%`
        cell.classList.toggle("is-playing", this.playingThrough)
        cell.classList.toggle("is-dragging", this.dragging)
      }
    }
  }

  // --- Scrub / drag --------------------------------------------------------

  startScrub(event) {
    event.preventDefault()
    this.dragging = true
    this.captureScrubPointer(event)
    this.stopPlayback()
    this.scrubMetrics = this.captureScrubMetrics()
    window.clearTimeout(this.advanceTimer)
    this.advanceTimer = null
    window.addEventListener("pointermove", this.boundPointerMove)
    window.addEventListener("pointerup", this.boundPointerUp)
    window.addEventListener("pointercancel", this.boundPointerUp)
    document.body.style.userSelect = "none"
    this.scrubAt(event.clientX)
  }

  onPointerMove(event) {
    this.lastScrubX = event.clientX
    if (!this.scrubRaf) {
      this.scrubRaf = requestAnimationFrame(() => {
        this.scrubRaf = 0
        this.scrubAt(this.lastScrubX)
      })
    }
  }

  onPointerUp() {
    const globalMs = this.currentGlobalMs()
    this.dragging = false
    this.releaseScrubPointer()
    this.removeDragListeners()
    if (this.scrubRaf) { cancelAnimationFrame(this.scrubRaf); this.scrubRaf = 0 }
    document.body.style.userSelect = ""
    this.scrubMetrics = null
    if (this.frames?.length) {
      this.showFrame(this.currentIndex)
      this.setTimelinePosition(globalMs, { source: "scrub-end" })
    }
    if (this.pendingMediaRefresh) {
      this.pendingMediaRefresh = false
      this.refreshManifestMedia({ reason: "post-scrub", force: true })
    }
    // Leave the playhead exactly where the scrub left it — do NOT recompute via
    // currentProgress(), which (frameStartedAt having just reset) would snap the
    // playhead and timecode back to the frame start (item 5).
    this.updatePlayhead(this.lastScrubProgress ?? this.currentProgress())
  }

  removeDragListeners() {
    window.removeEventListener("pointermove", this.boundPointerMove)
    window.removeEventListener("pointerup", this.boundPointerUp)
    window.removeEventListener("pointercancel", this.boundPointerUp)
  }

  captureScrubPointer(event) {
    const handle = event.currentTarget || event.target
    const pointerId = event.pointerId
    this.scrubPointer = { handle, pointerId }
    try { handle?.setPointerCapture?.(pointerId) } catch (_) { /* ignore */ }
  }

  releaseScrubPointer() {
    const pointer = this.scrubPointer
    if (!pointer) return
    try { pointer.handle?.releasePointerCapture?.(pointer.pointerId) } catch (_) { /* ignore */ }
    this.scrubPointer = null
  }

  captureScrubMetrics() {
    if (!this.stripInnerTarget || !this.cellOffsets) return null
    const rect = this.stripInnerTarget.getBoundingClientRect()
    return {
      innerLeft: rect.left,
      cellOffsets: [...this.cellOffsets],
      cellWidths: [...this.cellWidths],
    }
  }

  scrubAt(clientX) {
    const metrics = this.scrubMetrics || this.captureScrubMetrics()
    if (!metrics || !this.timeline) return
    const localX = clientX - metrics.innerLeft
    const globalMs = globalMsForStripX(this.timeline, metrics, localX)
    this.setTimelinePosition(globalMs, { source: "scrub" })
  }

  // --- Caption context + composer ------------------------------------------

  syncNoteContext(frameOrEvent = this.currentFrame) {
    const frame = frameOrEvent?.target ? this.currentFrame : frameOrEvent
    if (!frame) return

    const timeMs = Math.round(this.currentTimelinePosition().inFrameMs || 0)
    const audioFilename = frame.audio?.filename || ""
    const context = this.frameContextValue?.[frame.number] || {}

    if (this.hasNoteFrameNumberTarget) this.noteFrameNumberTarget.value = frame.number
    if (this.hasNoteTimeMsTarget) this.noteTimeMsTarget.value = timeMs
    if (this.hasNoteAudioFilenameTarget) this.noteAudioFilenameTarget.value = audioFilename
    if (this.hasNoteFrameIdTarget) this.noteFrameIdTarget.value = context.frame_id || ""
    if (this.hasNoteShotIdTarget) this.noteShotIdTarget.value = context.shot_id || ""
    if (this.hasNoteImageAssetIdTarget) this.noteImageAssetIdTarget.value = context.image_asset_id || ""
    if (this.hasNoteAudioAssetIdTarget) this.noteAudioAssetIdTarget.value = context.audio_asset_id || ""
    if (this.hasNoteAssetIdTarget) {
      this.noteAssetIdTarget.value = context.image_asset_id || context.audio_asset_id || ""
    }
    this.paintComposerAnchor(frame, timeMs)
    if (this.hasNoteContextTarget) {
      this.noteContextTarget.textContent = `${frame.label} · ${audioFilename || "silent/action frame"}`
    }
  }

  // The visible composer anchor chip only (hidden form values are written by
  // syncNoteContext on showFrame/submit and are already correct).
  paintComposerAnchor(frame, timeMs) {
    if (!this.hasComposerAnchorTarget || !frame) return
    const time = timeMs / 1000
    const m = Math.floor(time / 60)
    const r = (time - m * 60).toFixed(1).padStart(4, "0")
    const icon = frame.audio ? "🔊" : "🔇"
    const audioFilename = frame.audio?.filename || ""
    this.composerAnchorTarget.textContent = `${icon} ${frame.label} · ${m}:${r}${audioFilename ? ` · ${audioFilename}` : ""}`
  }

  // Called from the playback clock tick; throttled to ~1/s so the anchor chip
  // tracks the playhead mid-clip without per-frame churn.
  refreshComposerAnchor({ force = false } = {}) {
    if (!this.hasComposerAnchorTarget) return
    const now = performance.now()
    if (!force && this.lastAnchorPaintAt && now - this.lastAnchorPaintAt < 1000) return
    this.lastAnchorPaintAt = now
    const timeMs = Math.round(this.currentTimelinePosition().inFrameMs || 0)
    this.paintComposerAnchor(this.currentFrame, timeMs)
  }

  syncFrameSourceContext(frame) {
    const context = this.frameContextValue?.[frame.number] || {}
    const change = this.frameCompareChange(frame)
    const sourceLabel = [
      frame.label,
      context.shot_stable_key,
      context.image_asset_stable_id,
    ].filter(Boolean).join(" · ")

    if (this.hasSourceFrameLabelTarget) {
      const sequence = frame.sequence ? `SEQ ${frame.sequence.id} · ${frame.sequence.name}` : sourceLabel || frame.label
      this.sourceFrameLabelTarget.textContent = change ? `${frame.label} · ${this.frameChangeLabel(change)}` : sequence
    }
    if (this.hasSourceBeatTarget) {
      const sourceBeat = [
        context.source_location,
        context.beat_summary,
      ].filter(Boolean).join(" — ") || "No frame-specific beat has been synced yet."
      this.sourceBeatTarget.textContent = sourceBeat
    }
    if (this.hasSourceExcerptTarget) {
      this.sourceExcerptTarget.textContent = context.source_excerpt || "No frame-specific source excerpt has been synced yet."
    }
    if (this.hasFocusedPromptTarget) {
      this.focusedPromptTarget.textContent = context.focused_prompt || "No focused frame prompt has been synced yet."
    }
    if (this.hasReferenceMaterialTarget) {
      this.referenceMaterialTarget.textContent = context.reference_material || "No frame-specific reference material has been synced yet."
    }
    if (this.hasChangeContextTarget) {
      this.changeContextTarget.textContent = this.frameChangeText(change)
    }
    if (this.hasChangeTabTarget) {
      this.changeTabTarget.classList.toggle("has-version-change", Boolean(change))
    }
  }

  frameChangeLabel(change) {
    const subject = change?.kind_labels?.length ? change.kind_labels.join(", ") : "Review focus"
    const base = change?.base_label ? ` since ${change.base_label}` : ""
    return `${subject}${base}`
  }

  frameChangeText(change) {
    if (!change) return "No version-change note for this frame."

    const lines = [
      this.frameChangeLabel(change),
      "",
    ]
    if (change.summary) {
      lines.push(change.summary, "")
    }
    if (change.items?.length) {
      lines.push("What changed here:")
      change.items.forEach((item) => lines.push(`- ${item}`))
    } else {
      lines.push("No frame-specific changelog note matched this frame.")
    }
    return lines.join("\n").trim()
  }

  // Context drawer toggle + tabs
  toggleContext() {
    if (!this.hasContextBodyTarget) return
    const open = this.contextBodyTarget.hidden
    this.contextBodyTarget.hidden = !open
    if (this.hasContextChevronTarget) this.contextChevronTarget.textContent = open ? "▾" : "▸"
  }

  showContextTab(event) {
    const tab = event.currentTarget.dataset.contextTab
    this.contextTabTargets.forEach((el) => el.classList.toggle("is-active", el.dataset.contextTab === tab))
    this.contextPaneTargets.forEach((el) => { el.hidden = el.dataset.contextTab !== tab })
  }

  // --- Captions / karaoke (preserved) --------------------------------------

  renderCaption(frame) {
    if (!this.hasCaptionDialogueTarget || !this.hasCaptionCueTarget) return

    this.stopKaraoke()
    const captions = frame.captions || {}

    if (captions.cue) {
      this.captionCueTarget.innerHTML = `<span class="ms-pill ${isMusicCue(captions.cue) ? "is-music" : ""}">${this.escapeHtml(captions.cue)}</span>`
      this.captionCueTarget.hidden = false
    } else {
      this.captionCueTarget.innerHTML = ""
      this.captionCueTarget.hidden = true
    }

    const dialogue = captions.dialogue
    const words = Array.isArray(dialogue?.words) ? dialogue.words : []
    // Keep the CC band reserved (always visible) so it never silently vanishes
    // and reads as "broken". Show a quiet "no captions for this version" line
    // only when the WHOLE version lacks caption data (v1/v2); when the version
    // has captions but this particular frame doesn't, leave the band quietly
    // empty rather than nagging on every silent frame.
    if (this.hasCaptionsTarget) this.captionsTarget.hidden = false
    if (this.hasCaptionEmptyTarget) {
      this.captionEmptyTarget.hidden = this.versionHasCaptions()
    }
    if (words.length) {
      this.currentWords = [...words].sort((a, b) => a.startMs - b.startMs)
      const fillColor = SPEAKER_COLORS[dialogue.speaker] || DEFAULT_FILL_COLOR
      this.captionDialogueTarget.style.setProperty("--caption-fill", fillColor)
      const speaker = dialogue.speaker
        ? `<span class="ms-caption-speaker">${this.escapeHtml(dialogue.speaker)}:</span> `
        : ""
      const wordsHtml = this.currentWords.map((w, i) => this.wordHtml(w, i)).join(" ")
      this.captionDialogueTarget.innerHTML = speaker + wordsHtml
      this.wordSpans = Array.from(this.captionDialogueTarget.querySelectorAll(".ms-caption-word"))
      this.fillUnits = Array.from(this.captionDialogueTarget.querySelectorAll(".ms-caption-fill"))
        .map((el) => ({ el, startMs: Number(el.dataset.start), endMs: Number(el.dataset.end) }))
      this.captionDialogueTarget.hidden = false
    } else {
      this.currentWords = []
      this.wordSpans = []
      this.fillUnits = []
      this.captionDialogueTarget.innerHTML = ""
      this.captionDialogueTarget.hidden = true
    }
    this.activeWordIndex = -1
  }

  versionHasCaptions() {
    return Boolean(this.manifestHasCaptions)
  }

  // --- C1: real audio waveforms in the clip lane ---------------------------

  // Render real decoded peaks behind each clip's filename. Decoding is async
  // and cached per src (peaks survive zoom / strip-height changes — only the
  // canvas drawing is recomputed). Failures fall back to the plain block.
  drawAllWaveforms() {
    if (!this.hasStripInnerTarget) return
    this.waveformPeaks ||= new Map() // src -> Float32Array peaks (cached), or null = failed
    const blocks = Array.from(this.stripInnerTarget.querySelectorAll(".ms-studio__clip"))
    const toDecode = []
    blocks.forEach((block) => {
      const src = block.dataset.clipSrc
      const canvas = block.querySelector(".ms-studio__clip-wave")
      if (!canvas) return
      if (!src) { block.classList.add("is-plain"); return }
      const cached = this.waveformPeaks.get(src)
      if (cached) {
        // Already decoded — only the drawing is recomputed (cheap) on
        // zoom / strip-height change. No re-decode.
        this.paintWaveform(canvas, cached)
      } else if (cached === null) {
        block.classList.add("is-plain") // known-failed src; don't retry
      } else if (!this.waveformDecoding?.has(src) && !toDecode.some((d) => d.src === src)) {
        // Skip srcs already mid-fetch/decode — they're in neither the cache nor
        // the queue, so without this guard a re-render during the decode window
        // (zoom, strip-height drag, rail toggle) would double-enqueue them.
        toDecode.push({ src })
      }
    })
    // Decode lazily and with bounded concurrency so we never fire a thundering
    // herd of blob-redirect fetches at the DB (caused MySQL deadlocks) nor
    // block the main thread. Peaks are cached, so this runs once per src.
    this.queueWaveformDecode(toDecode.map((d) => d.src))
  }

  queueWaveformDecode(srcs) {
    this.waveformQueue ||= []
    this.waveformInFlight ||= 0
    this.waveformDecoding ||= new Set() // srcs currently mid-fetch/decode
    const seen = new Set(this.waveformQueue)
    srcs.forEach((src) => {
      if (!seen.has(src) && !this.waveformDecoding.has(src)) this.waveformQueue.push(src)
    })
    const MAX = 2
    const pump = () => {
      // Never decode (download full clips) while playing — it would starve the
      // playback audio. Hold the queue and resume when playback stops.
      if (this.playingThrough) { window.setTimeout(pump, 1200); return }
      while (this.waveformInFlight < MAX && this.waveformQueue.length) {
        const src = this.waveformQueue.shift()
        if (this.waveformPeaks.has(src) || this.waveformDecoding.has(src)) continue
        this.waveformInFlight += 1
        this.waveformDecoding.add(src)
        const controller = new AbortController()
        this.waveformAbortControllers ||= new Map()
        this.waveformAbortControllers.set(src, controller)
        this.decodeWaveform(src, controller.signal).then((peaks) => {
          this.waveformPeaks.set(src, peaks)
          this.repaintWaveform(src, peaks)
        }).catch((error) => {
          if (error?.name === "AbortError") return
          this.waveformPeaks.set(src, null)
          this.markClipPlain(src)
        }).finally(() => {
          this.waveformAbortControllers?.delete(src)
          this.waveformDecoding.delete(src)
          this.waveformInFlight -= 1
          pump()
        })
      }
    }
    pump()
  }

  abortWaveformDecodes() {
    this.waveformQueue = []
    this.waveformAbortControllers?.forEach((controller) => controller.abort())
  }

  repaintWaveform(src, peaks) {
    if (!this.hasStripInnerTarget) return
    // Paint EVERY block sharing this src — two clips can point at the same audio
    // file, and querySelector would leave the second one blank.
    const canvases = this.stripInnerTarget.querySelectorAll(`.ms-studio__clip[data-clip-src="${CSS.escape(src)}"] .ms-studio__clip-wave`)
    canvases.forEach((canvas) => this.paintWaveform(canvas, peaks))
  }

  markClipPlain(src) {
    if (!this.hasStripInnerTarget) return
    const blocks = this.stripInnerTarget.querySelectorAll(`.ms-studio__clip[data-clip-src="${CSS.escape(src)}"]`)
    blocks.forEach((block) => block.classList.add("is-plain"))
  }

  async decodeWaveform(src, signal = null) {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) throw new Error("no AudioContext")
    const response = await fetch(src, { signal })
    if (!response.ok) throw new Error(`audio ${response.status}`)
    const buffer = await response.arrayBuffer()
    // One reused decode-only context for the controller's lifetime; closed in
    // disconnect() so a Turbo visit doesn't leak an AudioContext.
    this.decodeCtx ||= new Ctx()
    const audio = await this.decodeCtx.decodeAudioData(buffer)
    return this.computePeaks(audio)
  }

  // Reduce PCM to ~480 peak buckets (max abs amplitude per bucket); enough for
  // a crisp bar render at any clip width without storing the full signal.
  computePeaks(audioBuffer, buckets = 480) {
    const channel = audioBuffer.getChannelData(0)
    const block = Math.max(1, Math.floor(channel.length / buckets))
    const peaks = new Float32Array(buckets)
    let max = 0
    for (let b = 0; b < buckets; b++) {
      let peak = 0
      const start = b * block
      const end = Math.min(channel.length, start + block)
      for (let i = start; i < end; i++) {
        const v = Math.abs(channel[i])
        if (v > peak) peak = v
      }
      peaks[b] = peak
      if (peak > max) max = peak
    }
    // Normalise so quiet scratch clips still show a visible waveform.
    if (max > 0) for (let b = 0; b < buckets; b++) peaks[b] /= max
    return peaks
  }

  paintWaveform(canvas, peaks) {
    const block = canvas.parentElement
    if (!block) return
    const dpr = window.devicePixelRatio || 1
    const cssW = block.clientWidth
    const cssH = block.clientHeight
    if (cssW < 2 || cssH < 2 || !peaks?.length) return
    canvas.width = Math.round(cssW * dpr)
    canvas.height = Math.round(cssH * dpr)
    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, cssW, cssH)
    const mid = cssH / 2
    const barW = 1.5
    const gap = 1
    const step = barW + gap
    const bars = Math.max(1, Math.floor(cssW / step))
    const ratio = peaks.length / bars
    ctx.fillStyle = "rgba(58,122,66,.45)" // --ms-green-deep at low alpha (sits behind the filename)
    for (let i = 0; i < bars; i++) {
      const peak = peaks[Math.floor(i * ratio)] || 0
      const h = Math.max(1, peak * (cssH - 2))
      ctx.fillRect(i * step + 1, mid - h / 2, barW, h)
    }
  }

  wordHtml(word, index) {
    const chars = Array.isArray(word.chars) ? word.chars : []
    if (chars.length) {
      const charSpans = chars
        .map((c) => `<span class="ms-caption-char ms-caption-fill" data-start="${c.startMs}" data-end="${c.endMs}" data-text="${this.escapeHtml(c.char)}">${this.escapeHtml(c.char)}</span>`)
        .join("")
      return `<span class="ms-caption-word" data-word-index="${index}">${charSpans}</span>`
    }
    return `<span class="ms-caption-word ms-caption-fill" data-word-index="${index}" data-start="${word.startMs}" data-end="${word.endMs}" data-text="${this.escapeHtml(word.word)}">${this.escapeHtml(word.word)}</span>`
  }

  startKaraoke() {
    if (!this.fillUnits.length || this.karaokeRaf) return

    const tick = () => {
      const nowMs = this.captionClockMs()
      this.paintFills(nowMs)
      const index = this.findActiveWord(nowMs)
      if (index !== this.activeWordIndex) {
        if (this.activeWordIndex >= 0) this.wordSpans[this.activeWordIndex]?.classList.remove("is-active")
        if (index >= 0) this.wordSpans[index]?.classList.add("is-active")
        this.activeWordIndex = index
      }
      this.karaokeRaf = this.karaokeClockRunning() ? requestAnimationFrame(tick) : null
    }
    this.karaokeRaf = requestAnimationFrame(tick)
  }

  karaokeClockRunning() {
    const eventAudio = this.captionAudioEvent?.audio
    if (eventAudio && !eventAudio.paused && !eventAudio.ended) return true
    return this.playingThrough || !this.audioTarget.paused
  }

  paintCaptionAt(inFrameMs = this.currentTimelinePosition().inFrameMs) {
    if (!this.fillUnits.length) return
    const nowMs = this.captionClockMs(inFrameMs)
    this.paintFills(nowMs)
    const index = this.findActiveWord(nowMs)
    if (index !== this.activeWordIndex) {
      if (this.activeWordIndex >= 0) this.wordSpans[this.activeWordIndex]?.classList.remove("is-active")
      if (index >= 0) this.wordSpans[index]?.classList.add("is-active")
      this.activeWordIndex = index
    }
  }

  captionClockMs(inFrameMs = null) {
    const event = this.captionAudioEvent
    const eventAudio = event?.audio
    if (eventAudio && !eventAudio.paused && !eventAudio.ended) {
      if (eventAudio.currentTime <= 0) return PLAYBACK_LEAD_MS
      const sourceInMs = Math.max(0, Number(event.sourceInMs || 0))
      return Math.max(0, (eventAudio.currentTime * 1000) - sourceInMs) + PLAYBACK_LEAD_MS
    }

    const audio = this.audioTarget
    if (this.currentFrame?.audio && audio && !audio.paused && !audio.ended) {
      if (audio.currentTime <= 0) return PLAYBACK_LEAD_MS
      return audio.currentTime * 1000 + PLAYBACK_LEAD_MS
    }
    const timelineInFrameMs = Number.isFinite(inFrameMs) ? inFrameMs : this.currentTimelinePosition().inFrameMs
    const audioOffsetMs = Number(this.currentFrame?.audioOffsetMs || 0)
    return Math.max(0, timelineInFrameMs - audioOffsetMs) + PLAYBACK_LEAD_MS
  }

  paintFills(nowMs) {
    if (!Number.isFinite(nowMs)) return
    this.fillUnits.forEach((unit) => {
      const duration = unit.endMs - unit.startMs
      let fill = 0
      if (nowMs >= unit.endMs) fill = 100
      else if (nowMs > unit.startMs && duration > 0) fill = ((nowMs - unit.startMs) / duration) * 100
      unit.el.style.setProperty("--fill", `${fill.toFixed(1)}%`)
    })
  }

  // --- Timecode clock (preserved logic; display format updated) ------------

  startClock() {
    if (this.clockRaf) return
    const tick = () => {
      if (this.playingThrough) {
        // Play-through: the wall-clock global timeline drives everything.
        this.playTick()
      } else {
        // Single-clip playback (step / seek autoplay): audio-driven within ONE
        // frame, capped at its slot. One source per tick → no jitter.
        const inFrameMs = this.currentInFrameMs()
        this.updatePositionLive(inFrameMs)
        this.updatePlayhead(inFrameMs / (this.currentFrame?.durationMs || 1))
        this.refreshComposerAnchor()
      }
      const running = this.playingThrough || !this.audioTarget.paused
      this.clockRaf = running ? requestAnimationFrame(tick) : null
    }
    this.clockRaf = requestAnimationFrame(tick)
  }

  stopClock() {
    if (this.clockRaf) {
      cancelAnimationFrame(this.clockRaf)
      this.clockRaf = null
    }
  }

  // `inFrameMs` is supplied by the clock tick (the single monotonic source) so
  // the timecode, the strip playhead and the SFX scheduler all read the exact
  // same global position. Falls back to recomputing when called standalone.
  updatePositionLive(inFrameMs = this.currentInFrameMs()) {
    this.updatePosition(inFrameMs)
    // SFX/ambient/music ride the same exact global playhead as the timecode.
    this.scheduleAudioEvents((this.frameStartMs?.[this.currentIndex] || 0) + inFrameMs)
  }

  // Rich readout (restored per live-use feedback): global timecode with
  // millisecond precision, the frame number (F02/55), and the in-frame offset
  // (+N.NNNs) — plus the total. `clipMs` is the elapsed time within the current
  // frame; `base` is that frame's global start.
  updatePosition(clipMs) {
    if (!this.hasPositionTarget) return
    const clamped = Math.max(0, clipMs)
    const base = this.frameStartMs?.[this.currentIndex] || 0
    const frame = this.currentFrame
    const frameNo = frame ? `${this.escapeHtml(frame.label)} <span class="is-frame-total">/${this.frames.length}</span>` : ""
    const offset = `+${(clamped / 1000).toFixed(3)}s`
    this.positionTarget.innerHTML =
      `${this.fmtTimeMs(base + clamped)}` +
      `<span class="is-frameno">${frameNo}</span>` +
      `<span class="is-offset">${offset}</span>` +
      `<span class="is-total">/ ${this.fmtTimeMs(this.totalMs || 0)}</span>`
  }

  // m:ss.s — coarse readout for the timeline meta + total length.
  fmtTime(ms) {
    const s = Math.max(0, ms) / 1000
    const m = Math.floor(s / 60)
    const r = (s - m * 60)
    return `${m}:${r.toFixed(1).padStart(4, "0")}`
  }

  // m:ss.mmm — millisecond-precision readout for the live timecode (restored
  // from the pre-redesign format).
  fmtTimeMs(ms) {
    const total = Math.max(0, ms)
    const m = Math.floor(total / 60000)
    const s = Math.floor((total % 60000) / 1000)
    const millis = Math.floor(total % 1000)
    return `${m}:${String(s).padStart(2, "0")}.${String(millis).padStart(3, "0")}`
  }

  syncTimelineMeta() {
    if (this.hasTimelineMetaTarget) {
      this.timelineMetaTarget.textContent = `${this.frames.length} frames · ${this.fmtTime(this.totalMs || 0)}`
    }
    if (this.hasFrameInputTotalTarget) this.frameInputTotalTarget.textContent = `/ ${this.frames.length}`
    this.syncCompareControls()
  }

  renderDebugHud() {
    if (!this.debugEnabled) return
    this.debugHud ||= (() => {
      const el = document.createElement("pre")
      el.className = "ms-studio__debug-hud ms-mono"
      this.element.appendChild(el)
      return el
    })()
    const position = this.currentTimelinePosition()
    this.debugHud.textContent = [
      `globalMs=${Math.round(this.currentGlobalMs())}`,
      `frame=${this.currentIndex + 1}/${this.frames?.length || 0}`,
      `inFrameMs=${Math.round(position.inFrameMs || 0)}`,
      `audio=${Math.round((this.audioTarget?.currentTime || 0) * 1000)}`,
      `playing=${this.playingThrough}`,
      `refresh=${this.lastMediaRefreshAt || 0}`,
      `error=${this.lastMediaError || "none"}`,
    ].join("\n")
  }

  stopKaraoke() {
    if (this.karaokeRaf) {
      cancelAnimationFrame(this.karaokeRaf)
      this.karaokeRaf = null
    }
    if (this.activeWordIndex >= 0) {
      this.wordSpans[this.activeWordIndex]?.classList.remove("is-active")
      this.activeWordIndex = -1
    }
  }

  findActiveWord(nowMs) {
    const words = this.currentWords
    let lo = 0
    let hi = words.length - 1
    let result = -1
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      if (words[mid].endMs <= nowMs) lo = mid + 1
      else if (words[mid].startMs > nowMs) hi = mid - 1
      else { result = mid; break }
    }
    return result
  }

  // --- Keyboard ------------------------------------------------------------

  onKeydown(event) {
    const active = document.activeElement || {}
    const tag = active.tagName
    // SELECT included so arrows operate the SPEED select natively.
    const typing = tag === "TEXTAREA" || tag === "INPUT" || tag === "SELECT"
    if (typing) {
      if (event.key === "Escape") active.blur?.()
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        const form = active.closest?.("form")
        if (form) { event.preventDefault(); form.requestSubmit() }
      }
      return
    }
    // Let Space activate a focused button/summary/[role=button] natively
    // instead of hijacking it for play·pause.
    if (event.key === " ") {
      const role = active.getAttribute?.("role")
      if (tag === "BUTTON" || tag === "SUMMARY" || role === "button") return
    }
    switch (event.key) {
      case " ": case "k": case "K": event.preventDefault(); this.togglePlay(); break
      case "ArrowLeft": event.preventDefault(); this.stepBy(event.shiftKey ? -10 : -1); break
      case "ArrowRight": event.preventDefault(); this.stepBy(event.shiftKey ? 10 : 1); break
      case "ArrowUp": event.preventDefault(); this.markerJump(-1); break
      case "ArrowDown": event.preventDefault(); this.markerJump(1); break
      case "Home": event.preventDefault(); this.firstFrame(); break
      case "End": event.preventDefault(); this.lastFrame(); break
      case "-": case "_": this.nudgeZoom(-0.2); break
      case "=": case "+": this.nudgeZoom(0.2); break
      case "r": case "R": this.replay(); break
      case "m": case "M": this.toggleMute(); break
      case "c": case "C": event.preventDefault(); this.focusComposer(); break
      case "g": case "G": event.preventDefault(); if (this.hasFrameInputTarget) { this.frameInputTarget.focus(); this.frameInputTarget.select() }; break
      case "f": case "F": event.preventDefault(); this.toggleMaximise(); break
      case "Escape": this.element.classList.contains("is-maximised") ? this.exitMaximise() : this.stopPlayback(); break
      default: break
    }
  }

  // Fill the whole viewport. Native Fullscreen API where supported (desktop /
  // Android — hides the browser chrome); always toggles the CSS full-viewport
  // class, which is the only path on iOS Safari (no Fullscreen on non-video
  // elements). Rotate the phone to landscape for a big player.
  toggleMaximise() {
    if (this.element.classList.contains("is-maximised")) { this.exitMaximise(); return }
    this.element.classList.add("is-maximised")
    if (this.element.requestFullscreen) {
      this.element.requestFullscreen().catch(() => { /* CSS fallback already applied */ })
    }
  }

  exitMaximise() {
    this.element.classList.remove("is-maximised")
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {})
    }
  }

  nudgeZoom(delta) {
    const next = Math.max(0.6, Math.min(2.6, +(this.stripZoomValue + delta).toFixed(2)))
    this.stripZoomValue = next
    if (this.hasZoomRangeTarget) this.zoomRangeTarget.value = next
  }

  focusComposer() {
    // The composer lives in the rail; if the rail is collapsed to its slim
    // strip the composer is hidden, so un-collapse first (S1) before focusing,
    // otherwise focus() is a no-op.
    if (this.railCollapsed) this.toggleRail()
    if (this.hasNoteBodyTarget) this.noteBodyTarget.focus()
  }

  // --- helpers -------------------------------------------------------------

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]
    ))
  }

  playIconHtml(playing) {
    return playing
      ? `<svg class="ms-studio__icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1.2"/><rect x="14" y="5" width="4" height="14" rx="1.2"/></svg>`
      : `<svg class="ms-studio__icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5l13 7-13 7z"/></svg>`
  }
  audioIconHtml() {
    return `<svg class="ms-studio__icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/></svg>`
  }
  silentIconHtml() {
    return `<svg class="ms-studio__icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M22 9l-5 6M17 9l5 6"/></svg>`
  }

  get currentFrame() {
    return this.frames[this.currentIndex]
  }
}
